/* Pufa Order Wizard v0.18.0 controller.
   Wraps the existing dynamic-picker (.iop, incl. the iop--size slider) and
   bundle-builder nodes into a gated, gamified step modal that matches the
   home-page quiz shell. It does NOT re-implement selection logic: it moves
   those nodes into the sheet, reads their state via a uniform step contract
   (which fixes the stuck size step), and submits the real product form only
   from the final step.

   Progressive enhancement: the CTA is hidden and inline pickers stay live until
   a successful build; only then do we reveal the CTA and set html.owz-on (which
   hides the inline pickers + ATC). JS-fail / kill-switch keep the native path. */
(function (root) {
  'use strict';

  // ── Pure, exported for tests ──────────────────────────────────────────────
  function isAutoAdvance(kind) { return kind === 'single' || kind === 'variant' || kind === 'choice'; }
  // The single definition of the min_picks contract. A picker with no attribute keeps the
  // legacy floor of 1 (cannot be emptied); an explicit 0 means genuinely optional. This
  // used to be spelled three different ways in three readers, so `min_picks: 0` worked
  // inside a group and silently floored back to 1 everywhere else.
  function minPicksOf(el) {
    var raw = el && el.getAttribute ? el.getAttribute('data-min-picks') : null;
    return raw == null || raw === '' ? 1 : (parseInt(raw, 10) || 0);
  }
  // Pure half of readStep's group branch: each child is gated on its OWN min, because a
  // shared count would let two weight picks satisfy a group whose style picker is empty.
  // `parts` keeps the children separate so the review can show one row each instead of
  // flattening "עדין, לבבות" into a single list you cannot decode.
  function readGroup(children) {
    var ready = true, parts = [], vals = [];
    (children || []).forEach(function (c) {
      var sel = c.selected || [];
      if (sel.length < (c.min || 0)) ready = false;
      var v = sel.join(', ') || c.emptyValue || '';
      if (v) parts.push({ key: c.key || '', value: v });
      vals.push.apply(vals, sel);
    });
    return { ready: ready, count: vals.length, value: vals.join(', '), parts: parts };
  }
  function stepComplete(kind, st) {
    st = st || {};
    // `st.min == null ? 1 : st.min` NOT `st.min || 1` - the latter swallows a legitimate 0.
    if (kind === 'multi') return (st.count || 0) >= (st.min == null ? 1 : st.min);
    // Fails closed: no readStep result = not ready.
    if (kind === 'group') return st.ready === true;
    if (kind === 'size') return !!st.value;     // seeded synchronously from .is-on
    if (kind === 'text') return true;           // optional by design
    if (kind === 'single' || kind === 'variant' || kind === 'choice' || kind === 'bundle') return !!st.value;
    return true;
  }
  // Expectation guard: shown on the review step when the shopper marked more jewelry
  // TYPES than the capsules they will receive (each capsule = ONE surprise piece drawn
  // from the marked types, not one of each). Returns '' when there is nothing to clarify.
  var MIX_NOTE_ONE = 'שימו לב 🥰 סימנתם {types} סוגי תכשיטים ובחרתם קפסולה אחת, כלומר תכשיט מפתיע אחד. נבחר לכם אחד מהסוגים שסימנתם. רוצים אחד מכל סוג? פשוט מוסיפים קפסולות ✨';
  var MIX_NOTE_MANY = 'שימו לב 🥰 סימנתם {types} סוגי תכשיטים ותקבלו {caps} תכשיטים. כל קפסולה מפתיעה באחד מהסוגים שסימנתם, לא בהכרח אחד מכל סוג. רוצים את כולם? פשוט מוסיפים קפסולות ✨';
  function mixNote(types, caps, tplOne, tplMany) {
    types = parseInt(types, 10) || 0; caps = parseInt(caps, 10) || 0;
    if (types <= 0 || caps <= 0 || types <= caps) return '';
    var tpl = caps === 1 ? (tplOne || MIX_NOTE_ONE) : (tplMany || MIX_NOTE_MANY);
    return tpl.replace(/\{types\}/g, types).replace(/\{caps\}/g, caps);
  }
  root.PufaWizard = root.PufaWizard || {};
  root.PufaWizard.stepComplete = stepComplete;
  root.PufaWizard.isAutoAdvance = isAutoAdvance;
  root.PufaWizard.minPicksOf = minPicksOf;
  root.PufaWizard.readGroup = readGroup;
  root.PufaWizard.mixNote = mixNote;
  if (typeof module !== 'undefined' && module.exports) { module.exports = { stepComplete, isAutoAdvance, mixNote, minPicksOf, readGroup }; }
  if (typeof window === 'undefined') return; // Node test import stops here.

  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var DESKTOP = 1024, ADVANCE_MS = 300, SWIPE_PX = 110, CONFETTI_MAX = 35;
  var FOCUSABLE = 'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])';

  function parseJSONTag(rootEl, sel) { var t = rootEl.querySelector(sel); if (!t) return null; try { return JSON.parse(t.textContent || ''); } catch (e) { return null; } }
  function findForm(rootEl) {
    var id = rootEl.getAttribute('data-form');
    if (id) { var f = document.getElementById(id); if (f && f.tagName === 'FORM') return f; }
    var pf = document.querySelector('product-form form[action*="/cart/add"]');
    return pf || document.querySelector('form[action*="/cart/add"]');
  }
  var _escEl; function esc(s) { _escEl = _escEl || document.createElement('div'); _escEl.textContent = (s == null ? '' : s); return _escEl.innerHTML; }

  function initImpl(rootEl) {
    if (rootEl.dataset.owzInit === '1') return;
    rootEl.dataset.owzInit = '1';

    // Kill-switch (leave inline path intact).
    try { if (localStorage.getItem('pufa_wizard_off') === '1' || /[?&]owz=off/.test(location.search)) return; } catch (e) {}

    var cfg = parseJSONTag(rootEl, '[data-owz-config]') || {};
    cfg.extra_steps = parseJSONTag(rootEl, '[data-owz-extra]') || [];
    var TX = cfg.text || {};
    var TEASE = {}, MIC = {};
    try { TEASE = typeof cfg.teases === 'string' ? JSON.parse(cfg.teases) : (cfg.teases || {}); } catch (e) {}
    try { MIC = typeof cfg.micro === 'string' ? JSON.parse(cfg.micro) : (cfg.micro || {}); } catch (e) {}
    function T(k, d) { return (TX[k] != null && TX[k] !== '') ? TX[k] : d; }
    function M(k, d) { return (MIC[k] != null && MIC[k] !== '') ? MIC[k] : d; }

    // There used to be a STYLE_IMG label->asset map here so attachStyleChips could
    // render PHOTO chips. That was a second, parallel implementation of the photo
    // style picker that dynamic-picker.liquid already provides, and it existed only
    // because the women's and men's products had no real style picker in their
    // metafield. Both are on picker_blocks_v2 now, so the photo path is gone and the
    // style picker lives in exactly one place. attachStyleChips survives as what it
    // always should have been: plain quick-fill pills for a free-text box (the party
    // kit's occasion chips).

    var form = findForm(rootEl);
    var overlay = rootEl.querySelector('[data-owz-overlay]');
    var sheet = rootEl.querySelector('[data-owz-sheet]');
    var bodyEl = rootEl.querySelector('[data-owz-body]');
    var segsEl = rootEl.querySelector('[data-owz-segs]');
    var countEl = rootEl.querySelector('[data-owz-count]');
    // Capsule fill meter (issue #19): the little capsule fills as steps complete;
    // the numeric שלב X/Y stays for screen readers (visually hidden).
    var capMeter = null;
    if (countEl) {
      capMeter = document.createElement('span');
      capMeter.className = 'owz-capfill';
      capMeter.setAttribute('aria-hidden', 'true');
      countEl.parentNode.insertBefore(capMeter, countEl);
      countEl.classList.add('owz-count--sr');
    }
    var backBtn = rootEl.querySelector('[data-owz-back]');
    var nextBtn = rootEl.querySelector('[data-owz-next]');
    var skipBtn = rootEl.querySelector('[data-owz-skip]');
    var priceEl = rootEl.querySelector('[data-owz-price]');
    var footEl = rootEl.querySelector('.owz-foot');
    var footErr = rootEl.querySelector('[data-owz-foot-err]');
    var climaxEl = rootEl.querySelector('[data-owz-climax]');
    var liveEl = rootEl.querySelector('[data-owz-live]');
    var cta = rootEl.querySelector('[data-owz-open]');
    if (!overlay || !bodyEl || !nextBtn || !sheet) return;
    // Back and close sit on the progress row (quiz parity), so Continue owns the whole footer.
    var progRow = rootEl.querySelector('.owz-progress'), closeBtn = rootEl.querySelector('.owz-close');
    if (progRow && backBtn) { progRow.insertBefore(backBtn, progRow.firstChild); backBtn.classList.add('owz-back--head'); }
    if (progRow && closeBtn) { progRow.appendChild(closeBtn); closeBtn.textContent = '✕'; }

    // ── Collect the adopted picker nodes as steps ────────────────────────────
    var steps = [];
    var pickerHost = document.querySelector('[data-dynamic-pickers]');
    var pickerSeq = 0;
    if (pickerHost) {
      // .iop-group wraps several pickers that must share ONE screen (style = weight +
      // motif). It is adopted as a single step; the pickers inside keep their own
      // sub-headers and their own min_picks.
      Array.prototype.forEach.call(pickerHost.querySelectorAll(':scope > .iop, :scope > .iop-group'), function (iop) {
        var kind = classify(iop);
        var titleEl = kind === 'group' ? iop.querySelector('.iop-group__title') : iop.querySelector('.iop__title');
        var title = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '';
        var teaseKey = kind === 'size' ? 'size'
          : (kind === 'text' || kind === 'group') ? 'style'
          : (pickerSeq === 0 ? 'type' : 'shade');
        if (kind !== 'size' && kind !== 'text' && kind !== 'group') pickerSeq++;
        steps.push({ kind: kind, node: iop, title: title, teaseKey: teaseKey });
      });
    }
    (cfg.extra_steps || []).forEach(function (ex) {
      steps.push({ kind: 'choice', extra: ex, title: ex.title || '', teaseKey: 'age', optional: ex.optional !== false });
    });
    var bundle = document.querySelector('bundle-builder');
    if (bundle) steps.push({ kind: 'bundle', node: bundle, title: T('quantity_title', 'כמה קסם נארוז לכם?'), teaseKey: 'quantity' });
    if (steps.length < 2) return; // keep inline if there is nothing real to wrap
    var reviewStep = { kind: 'review', title: T('review_title', 'הכול מוכן, בואו נסגור 🎁') };
    steps.push(reviewStep);

    // Optional explicit step order (by kind), for products whose picker DOM order
    // differs from the desired flow (e.g. party kit: event -> guests -> style -> size).
    // Review always stays last; ties keep their original relative order (stable).
    if (cfg.step_order && cfg.step_order.length) {
      var ord = cfg.step_order;
      steps = steps.map(function (s, i) { return { s: s, i: i }; }).sort(function (a, b) {
        var ka = a.s.kind === 'review' ? 1000 : ord.indexOf(a.s.kind); if (ka < 0) ka = 999;
        var kb = b.s.kind === 'review' ? 1000 : ord.indexOf(b.s.kind); if (kb < 0) kb = 999;
        return ka !== kb ? ka - kb : a.i - b.i;
      }).map(function (x) { return x.s; });
    }

    function classify(iop) {
      if (iop.classList.contains('iop-group')) return 'group';
      if (iop.classList.contains('iop--size') || iop.hasAttribute('data-iop-size')) return 'size';
      if (iop.classList.contains('iop--text')) return 'text';
      var m = iop.getAttribute('data-mode');
      if (m === 'property_multi') return 'multi';
      if (m === 'variant') return 'variant';
      return 'single';
    }

    var state = steps.map(function () { return {}; });
    var cur = 0, advanceTimer = null; // declared early: the seed-sync paints before nav

    // ── Build step DOM ───────────────────────────────────────────────────────
    steps.forEach(function (step, i) {
      var wrap = document.createElement('section');
      wrap.className = 'owz-step';
      wrap.setAttribute('data-owz-step', i);
      var h = document.createElement('h2');
      h.className = 'owz-step__h'; h.setAttribute('data-owz-step-h', ''); h.tabIndex = -1;
      h.textContent = step.title || (T('step', 'שלב') + ' ' + (i + 1));
      wrap.appendChild(h);

      if (step.kind === 'review') {
        renderReviewInto(wrap);
      } else if (step.kind === 'choice') {
        var ex = step.extra;
        var head = '<div class="owz-step__title">' + esc(step.title)
          + (step.optional ? ' <span class="owz-opt">(' + esc(T('optional', 'לא חובה')) + ')</span>' : '') + '</div>'
          + (ex.sub ? '<div class="owz-step__help">' + esc(ex.sub) + '</div>' : '')
          + '<div class="owz-chips" role="radiogroup" data-owz-choice></div>';
        wrap.insertAdjacentHTML('beforeend', head);
        var cw = wrap.querySelector('[data-owz-choice]');
        (ex.options || []).forEach(function (opt) {
          var label = typeof opt === 'string' ? opt : (opt.label || opt.value);
          var value = typeof opt === 'string' ? opt : (opt.value || opt.label);
          var chip = document.createElement('button');
          chip.type = 'button'; chip.className = 'owz-chip owz-choice-chip'; chip.textContent = label;
          chip.setAttribute('role', 'radio'); chip.setAttribute('aria-checked', 'false');
          chip.setAttribute('data-owz-chip', value);
          cw.appendChild(chip);
        });
      } else {
        // adopted picker (keeps its own visible header)
        var host = document.createElement('div'); host.className = 'owz-step__host';
        host.appendChild(step.node);
        if (step.kind === 'bundle') { var ph = step.node.querySelector('.pbb__heading'); if (ph && step.title) ph.textContent = step.title; }
        wrap.appendChild(host);
        if (step.kind === 'text') attachStyleChips(wrap, step.node, cfg.style_chips || []);
      }
      bodyEl.appendChild(wrap);
      step.el = wrap;
    });

    // ── Progress segments (one per step, excluding review) ───────────────────
    var segCount = steps.length - 1;
    var segNodes = [];
    for (var b = 0; b < segCount; b++) { var sg = document.createElement('span'); sg.className = 'owz-seg'; segsEl.appendChild(sg); segNodes.push(sg); }
    // "Quantity" step = the bundle if present, else the variant (size) picker, else the last non-review step.
    var qIndex = (function () {
      var bundleIdx = -1, lastIdx = -1;
      for (var i = 0; i < steps.length; i++) {
        if (steps[i].kind === 'review') continue;
        lastIdx = i;
        if (steps[i].kind === 'bundle' || steps[i].kind === 'variant') bundleIdx = i;
      }
      return bundleIdx >= 0 ? bundleIdx : lastIdx;
    })();
    // Variant picker node, cached once (used to price variant products that have no bundle).
    var variantNode = (function () { for (var i = 0; i < steps.length; i++) { if (steps[i].kind === 'variant' && steps[i].node) return steps[i].node; } return null; })();

    // ── Uniform step contract: live DOM read ─────────────────────────────────
    function readStep(step) {
      var n = step.node, k = step.kind;
      if (k === 'group') {
        // DOM -> descriptors, then the pure readGroup does the gating. `emptyValue` is
        // what an untouched picker MEANS ("surprise from all styles"), so a deliberate
        // non-choice travels to the review and the order instead of vanishing.
        var children = Array.prototype.map.call(n.querySelectorAll('.iop'), function (child) {
          // A card-less child (the free-text picker) is gated on nothing. It carries no
          // data-min-picks, and minPicksOf's absent-attribute default is the legacy floor
          // of 1 - which would make the group permanently unsatisfiable, since a textarea
          // has no cards to select. Only card pickers get a min.
          var hasCards = child.querySelectorAll('.iop-card').length > 0;
          return {
            key: child.getAttribute('data-prop-name') || '',
            min: hasCards ? minPicksOf(child) : 0,
            emptyValue: child.getAttribute('data-empty-value') || '',
            selected: Array.prototype.map.call(child.querySelectorAll('.iop-card--selected'), function (c) {
              return c.getAttribute('data-label') || c.getAttribute('data-value') || '';
            }).filter(Boolean)
          };
        });
        return readGroup(children);
      }
      if (k === 'multi' || k === 'single' || k === 'variant') {
        var sel = n.querySelectorAll('.iop-card--selected');
        return { count: sel.length, value: Array.prototype.map.call(sel, function (c) { return c.getAttribute('data-label') || c.getAttribute('data-value') || ''; }).filter(Boolean).join(', '),
          min: k === 'multi' ? minPicksOf(n) : 1 };
      }
      if (k === 'size') { var on = n.querySelector('.iop-size__step.is-on'); var inp = n.querySelector('[data-iop-size-input]');
        return { value: (on && (on.getAttribute('data-label') || '').trim()) || (inp && inp.value.trim()) || '' }; }
      if (k === 'text') { var ta = n.querySelector('.iop-textarea'); return { value: ta ? ta.value.trim() : '' }; }
      if (k === 'bundle') { var r = n.querySelector('.pbb-row.is-selected'); var lbl = r && r.querySelector('.pbb-row__label'); return { value: lbl ? lbl.textContent.trim() : '' }; }
      if (k === 'choice') { var c = step.el && step.el.querySelector('[data-owz-chip].is-on'); return { value: c ? c.getAttribute('data-owz-chip') : '' }; }
      return { value: '' };
    }
    function isReady(i) { var s = steps[i]; if (s.kind === 'review' || s.optional) return true; return stepComplete(s.kind, readStep(s)); }

    // Party kit helpers (0.66.0). Guests lower bound from the choice answer:
    // "25-50" -> 25, "50+" -> 50; "עד 25" is an open range with no honest floor -> 0.
    // 0 = unknown; every caller FAILS CLOSED (renders nothing) on 0.
    function guestsFloor() {
      var g = 0;
      steps.forEach(function (s, si) {
        if (s.kind !== 'choice' || !s.extra || !/אורח/.test(s.extra.property_name || '')) return;
        var m = String(state[si].value || '').match(/^\s*(\d+)/);
        if (m) g = parseInt(m[1], 10) || 0;
      });
      return g;
    }
    // Capsule count from a variant card's label ("50 קפסולות" -> 50); 0 = unparsable.
    function cardCaps(c) { return c ? (parseInt(((c.getAttribute('data-label') || '').match(/\d+/) || [0])[0], 10) || 0) : 0; }

    steps.forEach(function (step, i) {
      if (step.kind === 'review') return;
      function sync(fromTap) {
        var r = readStep(step);
        state[i].count = r.count != null ? r.count : (r.value ? 1 : 0);
        state[i].value = r.value || '';
        state[i].min = r.min == null ? 1 : r.min;   // not `|| 1`: a real 0 must survive
        state[i].parts = r.parts || null;           // grouped steps review one row per child
        if (step.kind === 'bundle' || step.kind === 'variant') updatePrice();
        if (i === cur) refreshNav();
        paintBeads();
        if (fromTap === true && isAutoAdvance(step.kind) && state[i].value && i !== qIndex) scheduleAdvance(i);
      }
      step._sync = sync;
      if (step.kind === 'group') attachGroupTextHint(step);
      if (step.kind === 'multi' || step.kind === 'single' || step.kind === 'variant' || step.kind === 'group') {
        // The picker fires option-picker:changed AND the click bubbles; coalesce the
        // click/keyup fallbacks into one trailing-tick sync so a tap runs sync once.
        // option-picker:changed bubbles, so a group hears all of its children.
        var pending = false;
        var syncSoon = function () { if (pending) return; pending = true; setTimeout(function () { pending = false; sync(true); }, 0); };
        step.node.addEventListener('option-picker:changed', function () { sync(true); });
        step.node.addEventListener('click', syncSoon);
        step.node.addEventListener('keyup', function (e) { if (e.key === 'Enter' || e.key === ' ') syncSoon(); });
      } else if (step.kind === 'size') {
        var ctrl = step.node.querySelector('[data-iop-size-control]') || step.node;
        step.node.addEventListener('click', function () { setTimeout(function () { sync(false); }, 0); });
        ctrl.addEventListener('pointerup', function () { setTimeout(function () { sync(false); }, 0); });
        ctrl.addEventListener('keyup', function () { setTimeout(function () { sync(false); }, 0); });
        // One subtree observer instead of one per .iop-size__step (a class flip fires it once, not twice).
        try { new MutationObserver(function () { sync(false); }).observe(ctrl, { attributes: true, attributeFilter: ['class'], subtree: true }); } catch (e) {}
      } else if (step.kind === 'text') {
        var ta = step.node.querySelector('.iop-textarea'); if (ta) ta.addEventListener('input', function () { sync(false); });
      } else if (step.kind === 'choice') {
        step.el.querySelectorAll('[data-owz-chip]').forEach(function (chip) {
          chip.addEventListener('click', function () {
            step.el.querySelectorAll('[data-owz-chip]').forEach(function (c) { c.classList.remove('is-on'); c.setAttribute('aria-checked', 'false'); });
            chip.classList.add('is-on'); chip.setAttribute('aria-checked', 'true');
            setHiddenProp(form, step.extra.property_name, chip.getAttribute('data-owz-chip'));
            sync(true);
          });
        });
      } else if (step.kind === 'bundle') {
        // Near-miss nudge (#13): fires at the decision margin instead of a permanent
        // "more = more bonus" headline. Only below the default 5-pack.
        // 0.57.0: the nudge is a real upgrade BUTTON - copy names the next
        // pack explicitly and one tap selects it (owner report: "2 more =
        // bonus" didn't tell people they had to move to the next bundle).
        var nudgeEl = document.createElement('button');
        nudgeEl.type = 'button';
        nudgeEl.className = 'owz-nudge';
        nudgeEl.hidden = true;
        nudgeEl.addEventListener('click', function () {
          var rows = Array.prototype.slice.call(step.node.querySelectorAll('.pbb-row'));
          rows.sort(function (a, b) { return (parseInt(a.getAttribute('data-qty'), 10) || 0) - (parseInt(b.getAttribute('data-qty'), 10) || 0); });
          var selIdx = rows.indexOf(step.node.querySelector('.pbb-row.is-selected'));
          var nxt = rows[selIdx + 1];
          if (nxt) nxt.click(); // bubbles: the row's own pop, stream and sync all fire
        });
        step.node.appendChild(nudgeEl);
        step._nudge = function () {
          var r = step.node.querySelector('.pbb-row.is-selected');
          var q = r ? parseInt(r.getAttribute('data-qty'), 10) : 0;
          var msg = q === 1 ? 'טיפ: בחבילה הבאה מקבלים קפסולה במתנה 🎁 <u>לשדרוג בלחיצה</u>'
            : q === 3 ? 'בחבילה הבאה מקבלים 2 קפסולות במתנה 🥰 <u>לשדרוג בלחיצה</u>'
            : '';
          nudgeEl.innerHTML = msg;
          nudgeEl.hidden = !msg;
        };
        step.node.addEventListener('click', function (e) {
          var row = e.target.closest('.pbb-row');
          setTimeout(function () {
            sync(false);
            if (step._nudge) step._nudge();
            // Bonus-unlock beat: upgrading to a tier with MORE free capsules pops a
            // one-shot gold "+N במתנה" chip on the row (no extra emoji burst - the
            // row's native pop + the feed-flight already cover particles). The
            // previous bonus is TRACKED, not read at click time: the picker's own
            // (earlier-registered, descendant) handler re-selects before ours runs.
            var nowSel = step.node.querySelector('.pbb-row.is-selected');
            var nowBonus = nowSel ? (parseInt(nowSel.getAttribute('data-bonus'), 10) || 0) : 0;
            if (!reduced && row && nowSel === row && step._lastBonus != null && nowBonus > step._lastBonus) {
              var oldChip = nowSel.querySelector('.owz-bonuspop'); if (oldChip) oldChip.remove();
              var bChip = document.createElement('span');
              bChip.className = 'owz-bonuspop';
              bChip.textContent = '+' + nowBonus + ' במתנה 🎁';
              nowSel.appendChild(bChip);
              haptic('beat');
              // The newly earned gold capsules drop into the row one by one.
              nowSel.querySelectorAll('.owz-caps__c--gold').forEach(function (g, gIdx) {
                if (!g.animate) return;
                try {
                  g.animate([
                    { transform: 'translateY(-10px) scale(.3)', opacity: 0 },
                    { transform: 'translateY(2px) scale(1.15)', opacity: 1, offset: .7 },
                    { transform: 'translateY(0) scale(1)', opacity: 1 }
                  ], { duration: 380, delay: gIdx * 90, easing: 'cubic-bezier(.34,1.56,.64,1)', fill: 'backwards' });
                } catch (e2) {}
              });
              setTimeout(function () { bChip.remove(); }, 1400);
            }
            step._lastBonus = nowBonus;
            // Capsule stream (owner idea, 0.53.0): on any pack CHANGE, the chosen
            // tier's capsules fly from the row's ladder into the gacha ball - the
            // bigger the bundle, the denser the stream (1 ball for a single, 15
            // for the 10+5). Crimson = paid, gold = bonus; each arrival gives the
            // ball a small gulp, the last one a big gulp + haptic tick. This IS
            // the flying celebration for the step (the chip stays on the row),
            // so the generic tap-spark is suppressed for the stream's duration.
            var nowQty = nowSel ? (parseInt(nowSel.getAttribute('data-qty'), 10) || 0) : 0;
            if (!reduced && row && nowSel === row && step._lastQty != null && nowQty !== step._lastQty
                && capMeter && window.PufaEmoji && window.PufaEmoji.flyTo && !document.hidden) {
              // Balls erupt from random points ACROSS THE TAPPED CARD (not the tiny
              // ladder dots - owner report: too subtle) and start ~instantly on tap.
              // Crimson paid balls first, the gold bonus ones as the finale.
              var totalBalls = nowQty + nowBonus;
              if (LOWEND) totalBalls = Math.min(totalBalls, 8);
              var goldFrom = Math.max(0, totalBalls - nowBonus); // gold finale survives thinning
              feedBusy = true;
              setTimeout(function () { feedBusy = false; }, totalBalls * 40 + 950);
              hapticStream(totalBalls);
              for (var bi = 0; bi < totalBalls; bi++) {
                (function (bi2) {
                  setTimeout(function () {
                    var r2 = nowSel.getBoundingClientRect(); // re-measure: sheet may scroll mid-stream
                    if (!r2.width) return;
                    var org = { left: r2.left + 8 + Math.random() * Math.max(1, r2.width - 16),
                                top: r2.top + 6 + Math.random() * Math.max(1, r2.height - 12),
                                width: 0, height: 0 };
                    window.PufaEmoji.flyTo(org, capMeter, {
                      chunk: true, size: 15, dur: 680, rise: 60 + Math.random() * 40,
                      color: bi2 >= goldFrom ? '#f0c440' : null,
                      onArrive: function () {
                        var last = bi2 === totalBalls - 1;
                        // no haptic() here - a vibrate call would cancel the
                        // hapticStream pattern running in landing rhythm
                        if (capMeter.animate && (!LOWEND || last)) {
                          try {
                            capMeter.animate([
                              { transform: 'scale(1)' },
                              { transform: 'scale(' + (last ? 1.3 : 1.12) + ')', offset: .4 },
                              { transform: 'scale(1)' }
                            ], { duration: last ? 300 : 180, easing: 'ease-out', composite: 'add' });
                          } catch (e4) {}
                        }
                      }
                    });
                  }, 30 + bi2 * 40);
                })(bi);
              }
            }
            if (nowSel) step._lastQty = nowQty;
            // NO auto-advance on the quantity step (owner decision, 0.51.1): it's the
            // money/comparison choice and the advance was eating the unlock beat.
            // The shopper leaves via the always-visible ממשיכים button.
          }, 0);
        });
        step._lastBonus = null; step._lastQty = null;
        setTimeout(function () { // after bundle default seed: nudge + bonus/qty baseline
          if (step._nudge) step._nudge();
          var s0 = step.node.querySelector('.pbb-row.is-selected');
          step._lastBonus = s0 ? (parseInt(s0.getAttribute('data-bonus'), 10) || 0) : 0;
          step._lastQty = s0 ? (parseInt(s0.getAttribute('data-qty'), 10) || 0) : 0;
        }, 200);
        // Capsule Ladder: render each tier's units as physical mini-capsules
        // (crimson = paid, gold = free bonus) so the ladder reads as loot, not
        // digits. Wizard-scope only by construction - Lucky Scoop pages never
        // run this code. Max deck today is 10 + 5; .owz-caps wraps if cramped.
        step.node.querySelectorAll('.pbb-row').forEach(function (r) {
          if (r.querySelector('.owz-caps')) return;
          var cq = parseInt(r.getAttribute('data-qty'), 10) || 0;
          var cb = parseInt(r.getAttribute('data-bonus'), 10) || 0;
          if (!cq) return;
          var capsWrap = document.createElement('span');
          capsWrap.className = 'owz-caps'; capsWrap.setAttribute('aria-hidden', 'true');
          var capsHtml = '';
          for (var ci = 0; ci < cq; ci++) capsHtml += '<i class="owz-caps__c"></i>';
          for (var gi = 0; gi < cb; gi++) capsHtml += '<i class="owz-caps__c owz-caps__c--gold"></i>';
          capsWrap.innerHTML = capsHtml;
          (r.querySelector('.pbb-row__content') || r).appendChild(capsWrap);
        });
        // ── Milestone Ladder (0.54.0): wizard-scope presentational overlay.
        // The hidden .pbb-rows stay the single source of truth (form commit,
        // price, review). The ladder drives them via NON-BUBBLING clicks, so
        // the legacy row-based beats (zero-rect sparks) never fire; the ladder
        // owns its own celebration. Any bad data or error = classic rows stay.
        (function buildLadder() {
          try {
            // Owner decision (0.56.0, after the judge panel): classic cards are
            // the default; the ladder stays behind ?ladder=1 (horizontal) /
            // ?ladder=v (vertical) for testing without a redeploy.
            var ladderMode = (location.search.match(/[?&]ladder=(\w+)/) || [])[1] || '';
            if (ladderMode !== '1' && ladderMode !== 'v') return;
            var rowsEl = step.node.querySelector('.pbb__rows');
            if (!rowsEl) return;
            var tiers = [];
            var rowList = Array.prototype.slice.call(step.node.querySelectorAll('.pbb-row'));
            for (var ti = 0; ti < rowList.length; ti++) {
              var rr = rowList[ti];
              var q = parseInt(rr.getAttribute('data-qty'), 10);
              if (!q || !rr.getAttribute('data-variant')) return; // classic rows stay
              var rimg = rr.querySelector('.pbb-row__img img');
              tiers.push({
                row: rr, qty: q,
                bonus: parseInt(rr.getAttribute('data-bonus'), 10) || 0,
                badge: ((rr.querySelector('.pbb-row__badge') || {}).textContent || '').trim(),
                img: rimg ? (rimg.getAttribute('src') || '') : '',
                pay: ((rr.querySelector('.pbb-row__pay') || {}).textContent || '').trim()
              });
            }
            if (tiers.length < 2) return;
            tiers.sort(function (a, b) { return a.qty - b.qty; });
            var N = tiers.length;

            var lad = document.createElement('div');
            lad.className = 'owz-ladder';
            var lh = '<div class="owz-ladder__track" role="radiogroup" aria-label="' + esc(step.title) + '">'
              + '<span class="owz-ladder__rail" aria-hidden="true"></span>'
              + '<span class="owz-ladder__fill" aria-hidden="true"></span>';
            for (var ni = 0; ni < N; ni++) {
              var t = tiers[ni];
              var pct = Math.round(ni / (N - 1) * 100);
              lh += '<button type="button" class="owz-ladder__node" role="radio" aria-checked="false"'
                + ' data-tier="' + ni + '" style="--owz-lpos:' + pct + '%"'
                + ' aria-label="' + esc(t.qty + (t.bonus ? ' ועוד ' + t.bonus + ' במתנה' : '') + ' קפסולות') + '">'
                + (t.badge ? '<span class="owz-ladder__badge">' + esc(t.badge) + '</span>' : '')
                // Brand tier art (the same images as the classic quantity cards);
                // emoji only as a fallback for products without tier images.
                + '<span class="owz-ladder__gift" aria-hidden="true">'
                + (t.img ? '<img src="' + esc(t.img) + '" alt="" loading="lazy" draggable="false">' : (t.bonus ? '🎁' : '💊'))
                + '</span>'
                + '<span class="owz-ladder__nlabel" aria-hidden="true">' + t.qty + (t.bonus ? '<b>+' + t.bonus + '</b>' : '') + '</span>'
                // Judge-panel consensus fix: every tier shows its shekel price
                // permanently, restoring the parallel price comparison the
                // classic cards had (no tap-to-discover-price on the money step).
                + (t.pay ? '<span class="owz-ladder__nprice" aria-hidden="true">' + esc(t.pay) + '</span>' : '')
                + '</button>';
            }
            lh += '</div>'
              + '<div class="owz-ladder__stepper">'
              + '<button type="button" class="owz-ladder__btn owz-ladder__btn--down" aria-label="חבילה קטנה יותר">−</button>'
              + '<span class="owz-ladder__count"><b class="owz-ladder__num">0</b><i>קפסולות</i></span>'
              + '<button type="button" class="owz-ladder__btn owz-ladder__btn--up" aria-label="חבילה גדולה יותר">+</button>'
              + '</div>'
              + '<div class="owz-ladder__receipt" aria-live="polite"></div>';
            lad.innerHTML = lh;
            // Vertical variant (owner exploration): ?ladder=v stacks the track
            // like a real climbing ladder - tier 1 at the bottom, chest on top.
            if (ladderMode === 'v') lad.classList.add('owz-ladder--v');
            step.node.classList.add('pbb--ladder');
            step.node.appendChild(lad);
            var nudgeHome = step.node.querySelector('.owz-nudge');
            if (nudgeHome) lad.appendChild(nudgeHome); // near-miss line lives under the receipt

            var fillEl = lad.querySelector('.owz-ladder__fill');
            var nodeEls = Array.prototype.slice.call(lad.querySelectorAll('.owz-ladder__node'));
            var numEl = lad.querySelector('.owz-ladder__num');
            var receiptEl = lad.querySelector('.owz-ladder__receipt');
            var upBtn = lad.querySelector('.owz-ladder__btn--up');
            var downBtn = lad.querySelector('.owz-ladder__btn--down');

            function curIdx() {
              for (var i = 0; i < N; i++) if (tiers[i].row.classList.contains('is-selected')) return i;
              return -1;
            }
            function paint(idx) {
              var t = tiers[idx];
              fillEl.style.setProperty('--owz-lfill', String(idx / (N - 1)));
              nodeEls.forEach(function (n, i) {
                n.classList.toggle('is-reached', i <= idx);
                n.classList.toggle('is-cur', i === idx);
                n.setAttribute('aria-checked', i === idx ? 'true' : 'false');
              });
              var newNum = String(t.qty + t.bonus);
              if (numEl.textContent !== newNum) {
                numEl.textContent = newNum;
                if (!reduced && numEl.animate) { try { numEl.animate([{ transform: 'scale(.75)', opacity: .4 }, { transform: 'scale(1.12)', opacity: 1, offset: .6 }, { transform: 'scale(1)' }], { duration: 260, easing: 'ease-out' }); } catch (e) {} }
              }
              var pay = ((t.row.querySelector('.pbb-row__pay') || {}).textContent || '').trim();
              var was = ((t.row.querySelector('.pbb-row__was') || {}).textContent || '').trim();
              var per = t.row.querySelector('.pbb-row__per');
              receiptEl.innerHTML = '<span class="owz-ladder__pays">משלמים ' + (was ? '<s>' + esc(was) + '</s> ' : '') + '<b>' + esc(pay) + '</b></span>'
                + '<span class="owz-ladder__gets">מקבלים <b>' + (t.qty + t.bonus) + ' קפסולות</b>' + (t.bonus ? ' <em class="owz-ladder__free">(' + t.bonus + ' חינם 🎁)</em>' : '') + '</span>'
                + (per ? '<span class="owz-ladder__per">' + per.innerHTML + '</span>' : '');
              downBtn.disabled = idx <= 0;
              upBtn.disabled = idx >= N - 1;
              nodeEls.forEach(function (n) { n.classList.remove('is-tease'); });
              if (idx < N - 1 && tiers[idx + 1].bonus > t.bonus) nodeEls[idx + 1].classList.add('is-tease');
            }
            function celebrate(idx) {
              var t = tiers[idx], node = nodeEls[idx];
              haptic('beat');
              if (reduced) return;
              // composite:'add' rides on top of the node's positioning transform,
              // so the same pop works in both the horizontal and vertical layouts.
              if (node.animate) { try { node.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.35)', offset: .5 }, { transform: 'scale(1)' }], { duration: 350, easing: 'cubic-bezier(.34,1.56,.64,1)', composite: 'add' }); } catch (e) {} }
              var chip = document.createElement('span');
              chip.className = 'owz-ladder__pop';
              chip.textContent = '+' + t.bonus + ' במתנה 🎁';
              node.appendChild(chip);
              setTimeout(function () { chip.remove(); }, 1400);
              if (capMeter && window.PufaEmoji && window.PufaEmoji.flyTo && !document.hidden) {
                var totalBalls = Math.min(t.qty + t.bonus, LOWEND ? 8 : 15);
                var goldFrom = Math.max(0, totalBalls - t.bonus); // gold finale survives thinning
                hapticStream(totalBalls);
                for (var bi = 0; bi < totalBalls; bi++) {
                  (function (b2) {
                    setTimeout(function () {
                      var r2 = node.getBoundingClientRect();
                      if (!r2.width) return;
                      window.PufaEmoji.flyTo({ left: r2.left + Math.random() * r2.width, top: r2.top + Math.random() * r2.height, width: 0, height: 0 }, capMeter, {
                        chunk: true, size: 15, dur: 680, rise: 60 + Math.random() * 40,
                        color: b2 >= goldFrom ? '#f0c440' : null,
                        onArrive: function () {
                          // no haptic() here - hapticStream's pattern carries
                          // the landing rhythm; a vibrate call would cancel it
                          if (capMeter.animate && (!LOWEND || b2 === totalBalls - 1)) { try { capMeter.animate([{ transform: 'scale(1)' }, { transform: 'scale(' + (b2 === totalBalls - 1 ? 1.3 : 1.12) + ')', offset: .4 }, { transform: 'scale(1)' }], { duration: b2 === totalBalls - 1 ? 300 : 180, easing: 'ease-out', composite: 'add' }); } catch (e4) {} }
                        }
                      });
                    }, 30 + b2 * 40);
                  })(bi);
                }
              }
            }
            function setTier(idx, source) {
              idx = Math.max(0, Math.min(idx, N - 1));
              var prev = curIdx();
              if (idx === prev) return;
              var t = tiers[idx];
              try { t.row.dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true })); } catch (e) { t.row.click(); }
              paint(idx);
              if (step._sync) step._sync(false);
              if (step._nudge) step._nudge();
              haptic('tick');
              var pay = ((t.row.querySelector('.pbb-row__pay') || {}).textContent || '').trim();
              announce((t.qty + t.bonus) + ' קפסולות, ' + pay);
              if (prev >= 0 && t.bonus > tiers[prev].bonus) celebrate(idx);
              track('pack', { qty: t.qty, bonus: t.bonus, dir: idx > prev ? 'up' : 'down', ui: 'ladder', source: source || '' });
            }
            upBtn.addEventListener('click', function () { setTier(curIdx() + 1, 'stepper'); });
            downBtn.addEventListener('click', function () { setTier(curIdx() - 1, 'stepper'); });
            nodeEls.forEach(function (n) {
              n.addEventListener('click', function () { setTier(parseInt(n.getAttribute('data-tier'), 10), 'node'); });
            });
            lad.querySelector('.owz-ladder__track').addEventListener('keydown', function (e) {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); setTier(curIdx() + 1, 'key'); }
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); setTier(curIdx() - 1, 'key'); }
            });
            var seed = curIdx(); if (seed < 0) seed = 0;
            lad.classList.add('no-fx'); paint(seed);
            requestAnimationFrame(function () { lad.classList.remove('no-fx'); });
            // Entrance: first activation runs the fill 0 -> default so the
            // pre-earned gifts light up one by one (endowed progress).
            var entered = false;
            step._enterFx = function () {
              if (entered) return; entered = true;
              if (reduced) return;
              var idx = curIdx(); if (idx < 0) idx = 0;
              lad.classList.add('no-fx');
              fillEl.style.setProperty('--owz-lfill', '0');
              nodeEls.forEach(function (n) { n.classList.remove('is-reached', 'is-cur'); });
              void fillEl.offsetWidth;
              lad.classList.remove('no-fx');
              setTimeout(function () {
                nodeEls.forEach(function (n, i) { if (i <= idx) n.style.transitionDelay = (i * 60) + 'ms'; });
                paint(idx);
                setTimeout(function () { nodeEls.forEach(function (n) { n.style.transitionDelay = ''; }); }, 800);
              }, 60);
            };
          } catch (eL) {
            step.node.classList.remove('pbb--ladder');
            var l0 = step.node.querySelector('.owz-ladder'); if (l0) l0.remove();
          }
        })();
      }
      // ── Party-kit quantity beats (0.66.0) ─────────────────────────────────
      // Only when the QUANTITY step is a variant picker and the product has no
      // bundle step (today: the party kit). The custom-capsules bundle path above
      // is untouched by construction. Two beats:
      // 1. Guests-vs-kit near-miss nudge: guests floor > selected kit's capsules
      //    -> one-tap upgrade to the smallest kit that covers everyone. All
      //    numbers parse from what the shopper sees; unparsable = no nudge.
      // 2. Capped capsule stream into the gacha ball on kit selection - the
      //    party-sized cousin of the bundle stream (5-10 balls, LOWEND 6).
      if (step.kind === 'variant' && i === qIndex && !bundle) {
        var vNudge = document.createElement('button');
        vNudge.type = 'button'; vNudge.className = 'owz-nudge'; vNudge.hidden = true;
        vNudge.addEventListener('click', function () { if (vNudge._target) vNudge._target.click(); });
        step.node.appendChild(vNudge);
        step._nudge = function () {
          var msg = '', target = null;
          var sel = step.node.querySelector('.iop-card--selected');
          var guests = guestsFloor();
          var q = cardCaps(sel);
          if (sel && guests > 0 && q > 0 && q < guests) {
            var best = null, bestQ = Infinity;
            step.node.querySelectorAll('.iop-card:not(.iop-card--disabled)').forEach(function (c) {
              var cq = cardCaps(c); if (cq >= guests && cq < bestQ) { best = c; bestQ = cq; }
            });
            if (best) { target = best; msg = 'סימנתם ' + guests + '+ אורחים 🙈 שתהיה הפתעה לכל אחד? <u>לשדרוג ל-' + bestQ + ' קפסולות בלחיצה</u>'; }
          }
          vNudge._target = target; vNudge.innerHTML = msg; vNudge.hidden = !msg;
        };
        step.node.addEventListener('click', function (e) {
          var card = e.target.closest('.iop-card');
          setTimeout(function () {
            if (step._nudge) step._nudge();
            var sel = step.node.querySelector('.iop-card--selected');
            if (!card || !sel || sel !== card) return;
            var q = cardCaps(sel);
            if (!q || q === step._lastVQty) return;
            step._lastVQty = q;
            if (reduced || document.hidden || !capMeter || !window.PufaEmoji || !window.PufaEmoji.flyTo) return;
            var totalBalls = Math.min(LOWEND ? 6 : 10, Math.max(5, Math.round(q / 7)));
            feedBusy = true;
            setTimeout(function () { feedBusy = false; }, totalBalls * 40 + 950);
            hapticStream(totalBalls);
            for (var bi = 0; bi < totalBalls; bi++) {
              (function (b2) {
                setTimeout(function () {
                  var r2 = sel.getBoundingClientRect();
                  if (!r2.width) return;
                  window.PufaEmoji.flyTo({ left: r2.left + 8 + Math.random() * Math.max(1, r2.width - 16),
                    top: r2.top + 6 + Math.random() * Math.max(1, r2.height - 12), width: 0, height: 0 }, capMeter, {
                    chunk: true, size: 15, dur: 680, rise: 60 + Math.random() * 40,
                    onArrive: function () {
                      if (capMeter.animate && (!LOWEND || b2 === totalBalls - 1)) {
                        try { capMeter.animate([{ transform: 'scale(1)' }, { transform: 'scale(' + (b2 === totalBalls - 1 ? 1.3 : 1.12) + ')', offset: .4 }, { transform: 'scale(1)' }], { duration: b2 === totalBalls - 1 ? 300 : 180, easing: 'ease-out', composite: 'add' }); } catch (e4) {}
                      }
                    }
                  });
                }, 30 + b2 * 40);
              })(bi);
            }
          }, 0);
        });
        setTimeout(function () { var s0 = step.node.querySelector('.iop-card--selected'); if (s0) step._lastVQty = cardCaps(s0); if (step._nudge) step._nudge(); }, 200);
      }
      sync(false); // SYNCHRONOUS seed (size + preselected = complete on entry)
    });

    // ── Universal option-thrill (delegated, additive) ────────────────────────
    function owzPing(el, opts) {
      if (reduced || !el) return; opts = opts || {};
      el.classList.remove('owz-pop'); void el.offsetWidth; el.classList.add('owz-pop');
      setTimeout(function () { el.classList.remove('owz-pop'); }, 280);
      haptic('tick');
      if (opts.spark === false || !window.PufaEmoji) return;
      window.PufaEmoji.burst(el.getBoundingClientRect(), {
        count: Math.min(opts.count || 4, 5), glyphs: opts.glyphs || ['✨', '🎁', '💫'],
        spread: 'center', size: 15, dist: function () { return 24 + Math.random() * 12; }, life: 720, dur: 600
      });
    }
    // Per-preset particle sets: the tapped style card's LABEL picks its own glyphs.
    // "Eat" flight: a spark arcs from fromEl up into the gacha ball, which gulps it
    // with a scale pop (one flight at a time). Fired on selection taps AND Continue.
    var feedBusy = false;
    // Every ball landing buzzes (owner ask, 0.57.0): ONE pre-computed vibration
    // pattern in the stream's arrival rhythm. Per-arrival haptic() calls don't
    // work here - the 40ms throttle eats half of them, and each
    // navigator.vibrate call CANCELS the previous one on Android, so only a
    // single pattern can make N landings each feel like a tap.
    // 10ms buzz + 30ms gap per ball = the 40ms launch stagger; stronger finale.
    function hapticStream(count) {
      if (cfg.haptics === false || !navigator.vibrate || document.hidden || count < 1) return;
      var pat = [];
      for (var hi = 0; hi < count - 1; hi++) pat.push(10, 30);
      pat.push(22);
      // first ball leaves ~30ms after the tap and flies ~680ms
      setTimeout(function () { try { navigator.vibrate(pat); } catch (e) {} }, 700);
    }
    // Low-end device gate (0.61.0): <=4 cores or <=4GB RAM gets a thinner
    // celebration - 8 balls max (gold finale preserved) and the gacha-ball
    // gulp pop only on the final landing. Measured: the full 15-ball stream
    // at 6x CPU throttle ran at 29fps with 117ms stalls; the thinned stream
    // keeps the moment without the jank. Everyone else gets the full show.
    var LOWEND = (navigator.hardwareConcurrency || 8) <= 4 || (navigator.deviceMemory || 8) <= 4;
    function feedFrom(fromEl, glyph) {
      if (!capMeter || reduced || !fromEl || !window.PufaEmoji || !window.PufaEmoji.flyTo || feedBusy) return;
      feedBusy = true; setTimeout(function () { feedBusy = false; }, 700);
      // elements, not rects: flyTo re-measures the target each frame, so the flight
      // lands on the ball even when the desktop modal resizes mid-air
      window.PufaEmoji.flyTo(fromEl, capMeter, {
        glyph: glyph || '✨', size: 26, dur: 950,
        onArrive: function () {
          haptic('tick');
          if (capMeter.animate) {
            try {
              capMeter.animate([
                { transform: 'scale(1)' }, { transform: 'scale(1.3)', offset: 0.4 }, { transform: 'scale(1)' }
              ], { duration: 300, easing: 'ease-out', composite: 'add' });
            } catch (e) {}
          }
        }
      });
    }
    var PRESET_GLYPHS = {
      'פרחים': ['🌸', '💮', '✨'], 'פרחוני': ['🌸', '💮', '✨'], 'לבבות': ['❤️', '💗', '🫶'],
      'עדין': ['✨', '🤍', '🫧'], "צ'אנקי": ['💎', '🔗', '🔥'], 'חיות': ['🦋', '🐾', '✨'],
      'זרקונים': ['💎', '✨', '💫'],
      // Party-kit occasion chips (0.66.0). Keys are EXACT-match against the chip
      // labels in the product's wizard_config metafield (style_chips) - if a label
      // is edited in the admin, update it here too or the tap falls back to the
      // generic sparks (safe, just less fun).
      'יום הולדת': ['🎂', '🎈', '🎉'], 'בת מצווה': ['👑', '💖', '✨'], 'בר מצווה': ['🕺', '🎉', '✨'],
      'מסיבת רווקות': ['💍', '🥂', '🎉'], 'סוף שנה': ['🎓', '🎉', '✨']
    };
    bodyEl.addEventListener('click', function (e) {
      var el = e.target.closest('.iop-card:not(.iop-card--disabled), .iop-size__step, .owz-chip, .pbb-row');
      if (!el) return;
      // a refused tap leaves the card selected and the picker sitting exactly at its minimum
      var refused = el.matches('.iop-card--selected') && el.closest('.iop--needpick');
      if (refused && refused.querySelectorAll('.iop-card--selected').length > minPicksOf(refused)) refused = null;
      if (refused) { flashErr(refused.getAttribute('data-error-message') || M('gate_multi', 'בחרי לפחות אחד 👇')); track('blocked', { step: cur + 1, kind: 'last_option' }); return; }
      var nativeBurst = el.matches('.pbb-row') || (el.matches('.iop-card') && el.hasAttribute('data-pop'));
      var styleLbl = el.getAttribute('data-owz-style');
      owzPing(el, { spark: !nativeBurst, glyphs: styleLbl ? PRESET_GLYPHS[styleLbl] : undefined });
      // Feed the machine: while the current step is satisfied, every selection tap
      // sends a spark arcing up into the gacha ball.
      setTimeout(function () {
        var st = steps[cur];
        if (!st || st.kind === 'review') return;
        if (!isReady(cur)) return;
        feedFrom(el, (styleLbl && PRESET_GLYPHS[styleLbl]) ? PRESET_GLYPHS[styleLbl][0] : '✨');
      }, 120);
    });

    // ── Navigation ───────────────────────────────────────────────────────────
    function scheduleAdvance(i, ms) {
      if (i !== cur) return; clearTimeout(advanceTimer);
      advanceTimer = setTimeout(function () { if (cur === i && cur < steps.length - 1) goTo(cur + 1, true, 'fwd'); }, reduced ? 0 : (ms || ADVANCE_MS));
    }
    function refreshNav() {
      var step = steps[cur];
      backBtn.hidden = cur === 0;
      var isReview = step.kind === 'review';
      nextBtn.innerHTML = '<span class="owz-next__fill">' + esc(isReview ? T('final', 'יאללה, מוסיפים לסל 🎁') : T('continue', 'ממשיכים')) + '</span>';
      nextBtn.classList.toggle('is-final', isReview);
      var ready = isReview || isReady(cur);
      nextBtn.setAttribute('data-disabled', ready ? '0' : '1');
      nextBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
      var canSkip = cur < qIndex; // any preference step can jump straight to the quantity step
      skipBtn.hidden = !canSkip;
      // refreshNav also runs during init, before the note exists. nbsp: the theme hides an empty <p>, and the row must keep its height
      if (footNote) { footNote.hidden = canSkip; footNote.textContent = ((isReview && trustTiles) ? '' : noteLine) || '\u00a0'; }
      skipBtn.textContent = T('skip', 'תנו לפופה לבחור בשבילכם ✨');
      priceEl.hidden = !priceReady;
    }
    var footNote = document.createElement('p'); footNote.className = 'owz-foot__note'; footNote.hidden = true;
    var trustOn = cfg.trust_enabled !== false && cfg.trust && cfg.trust.length;
    var trustTiles = trustOn && cfg.trust.length > 2; // 3+ items stay as tiles on the review: they carry terms
    var noteLine = !trustOn ? '' : cfg.trust.map(function (it) { return (trustTiles ? '' : it.icon + ' ') + it.title; }).join('  ·  ');
    skipBtn.parentNode.insertBefore(footNote, skipBtn.nextSibling);
    // The capsules she has collected so far, drawn big under a short step (colour, age)
    // so the space reads as progress, not as a blank. Same count and colours as the rail.
    var pileEl = document.createElement('div'); pileEl.className = 'owz-pile'; pileEl.setAttribute('aria-hidden', 'true');
    for (var pb = 0; pb < segCount; pb++) pileEl.appendChild(document.createElement('i'));
    var heroEl = null;
    function placePile(step) {
      var cards = step.el.querySelectorAll('.iop-card').length;
      var isShort = step.kind === 'choice' || step.kind === 'size' || step.kind === 'text' || (step.kind !== 'group' && step.kind !== 'review' && step.kind !== 'bundle' && cards > 0 && cards <= 2);
      step.el.classList.toggle('owz-step--short', isShort);
      if (pileEl.parentNode) pileEl.parentNode.removeChild(pileEl);
      if (heroEl && heroEl.parentNode) heroEl.parentNode.removeChild(heroEl);
      if (!isShort) return;
      if (cur === 0) {
        var og = document.querySelector('meta[property="og:image"]');
        if (!og || !og.content) return;
        if (!heroEl) { heroEl = document.createElement('img'); heroEl.className = 'owz-hero'; heroEl.alt = ''; heroEl.setAttribute('aria-hidden', 'true'); heroEl.src = og.content.replace(/^http:/, 'https:'); }
        step.el.appendChild(heroEl); return;
      }
      step.el.appendChild(pileEl);
      for (var i = 0; i < pileEl.children.length; i++) pileEl.children[i].classList.toggle('is-in', i < cur);
    }
    function paintBeads() {
      var TOTAL = segCount;
      for (var i = 0; i < TOTAL; i++) {
        segNodes[i].classList.toggle('is-done', i <= cur); // position-based fill
        segNodes[i].classList.toggle('is-cur', i === cur);
      }
      var pos = Math.max(1, Math.min(cur + 1, TOTAL));
      countEl.textContent = (steps[cur].kind === 'review') ? '🎁' : (T('step', 'שלב') + ' ' + pos + ' / ' + TOTAL);
      if (capMeter) {
        var full = steps[cur].kind === 'review';
        capMeter.style.setProperty('--owz-fillpct', (full ? 100 : Math.round(pos / TOTAL * 100)) + '%');
        capMeter.classList.toggle('is-full', full);
      }
    }
    function goTo(i, userInitiated, direction) {
      if (i < 0 || i >= steps.length) return;
      var forward = direction !== 'back';
      bodyEl.style.setProperty('--owz-enter-x', reduced ? '0px' : (forward ? '-24px' : '24px'));
      if (steps[cur] && steps[cur].el) { var old = steps[cur].el; old.classList.remove('is-active'); }
      cur = i;
      var step = steps[cur];
      step.el.classList.add('is-active');
      if (step._enterFx) step._enterFx();
      if (step.kind === 'variant' && step._nudge) step._nudge(); // guests may have changed since last visit
      if (step.kind === 'review') { renderReviewInto(step.el, true); sealOnce(); }
      bodyEl.scrollTop = 0;
      refreshNav(); paintBeads(); placePile(step);
      var hh = step.el.querySelector('[data-owz-step-h]');
      if (hh) { if (!hh.id) hh.id = 'owzh-' + (cfg.product_id || 'p') + '-' + cur; overlay.setAttribute('aria-labelledby', hh.id); }
      announce((T('step', 'שלב') + ' ' + Math.min(cur + 1, segCount) + ' מתוך ' + segCount));
      if (hh) { var f = function () { try { hh.focus({ preventScroll: true }); } catch (e) { try { hh.focus(); } catch (e2) {} } }; reduced ? f() : requestAnimationFrame(function () { requestAnimationFrame(f); }); }
      haptic('soft');
      save();
      track('step', { step: cur + 1, kind: step.kind });
      // Here, not in refreshNav(): refreshNav runs on every state change, so it rebuilt the
      // cue (and the button HTML) on every space bar press inside the free-text box.
      recheckMoreCue();
    }
    function advanceFrom(i) { if (i === cur && cur < steps.length - 1) goTo(cur + 1, true, 'fwd'); }

    // Make the Continue button feel alive on press: a springy bounce + an emoji burst.
    function pressNext() {
      if (reduced) return;
      nextBtn.classList.remove('owz-next-bounce'); void nextBtn.offsetWidth; nextBtn.classList.add('owz-next-bounce');
      setTimeout(function () { nextBtn.classList.remove('owz-next-bounce'); }, 520);
      haptic('beat');
    }
    function nextEmoji() {
      if (reduced || !window.PufaEmoji) return;
      window.PufaEmoji.burst(nextBtn.getBoundingClientRect(), {
        count: 12, spread: 'surface', rise: 34, spin: 120, life: 1000, dur: 850,
        glyphs: ['✨', '🎁', '💫', '🎉', '💖', '🥳'],
        size: function () { return 16 + Math.random() * 12; },
        dist: function () { return 32 + Math.random() * 52; }
      });
    }
    // Scroll-more cue: fade + bouncing chevron appears when the body has content below the fold.
    var footEl = sheet.querySelector('.owz-foot');
    var moreCue = document.createElement('button');
    moreCue.type = 'button'; moreCue.className = 'owz-morecue'; moreCue.tabIndex = -1; moreCue.setAttribute('aria-hidden', 'true');
    moreCue.innerHTML = '<span class="owz-morecue__chev" aria-hidden="true">' + esc(M('more_below', 'יש עוד למטה')) + '</span>';
    moreCue.addEventListener('click', function () { bodyEl.scrollBy({ top: Math.round(bodyEl.clientHeight * 0.7), behavior: reduced ? 'auto' : 'smooth' }); });
    (footEl || bodyEl).appendChild(moreCue);
    function updateMoreCue() { sheet.classList.toggle('owz-has-more', (bodyEl.scrollHeight - bodyEl.scrollTop - bodyEl.clientHeight) > 10); }
    bodyEl.addEventListener('scroll', updateMoreCue, { passive: true });
    window.addEventListener('resize', updateMoreCue);
    // The cue used to be evaluated only on scroll/resize, so landing on a step that
    // overflows showed nothing until you had already scrolled - exactly when the hint
    // was needed it was absent. refreshNav() calls this on every step change; rAF lets
    // the incoming step lay out first and the later pass catches lazy images.
    function recheckMoreCue() {
      requestAnimationFrame(updateMoreCue);
      setTimeout(updateMoreCue, 260);
    }

    nextBtn.addEventListener('click', function () {
      var step = steps[cur];
      if (step.kind === 'review') { pressNext(); lockIn(); return; }
      if (nextBtn.getAttribute('data-disabled') === '1') {
        // A grouped step names the picker that is actually blocking, otherwise
        // "choose at least one" points at a screen where something IS chosen.
        var msg;
        if (step.kind === 'group') {
          var blocking = null;
          Array.prototype.forEach.call(step.node.querySelectorAll('.iop'), function (child) {
            if (blocking) return;
            if (child.querySelectorAll('.iop-card--selected').length < minPicksOf(child)) blocking = child;
          });
          // data-error-message, not the rendered element's textContent: showError() may have
          // overwritten that with the MAXIMUM-picks warning, and the gate would then parrot
          // a maximum back as if it were a minimum.
          msg = (blocking && blocking.getAttribute('data-error-message')) || M('gate_multi', 'בחרי לפחות אחד 👇');
        } else {
          msg = step.kind === 'multi' ? M('gate_multi', 'בחרו לפחות אחד 👇') : (step.kind === 'choice' ? M('gate_choice', 'בחרו כדי להמשיך') : M('gate_pick', 'רגע, בחרו אחד קודם 🥰'));
        }
        // Point at the thing that needs answering instead of printing an error.
        // flashErr() unhid a footer row, which grew the footer, which shrank the scroll
        // body - so a gate press flashed BOTH a message and a scrollbar. The pulse is
        // silent in layout terms. announce() still carries the words for screen readers.
        announce(msg); flashErr(msg); track('blocked', { step: cur + 1, kind: step.kind });
        pulseNeeded(step.kind === 'group' ? blocking : (step.el && step.el.querySelector('.iop')) || step.el);
        return;
      }
      if (cur < steps.length - 1) {
        // The Continue button IS a chunk of the bar: a ghost of it lifts off, shrinks
        // to bead size and merges into the bead it just completed (live-tracked), the
        // bead pulses, and the gacha ball's fill rises via the CSS var transition.
        if (!reduced && window.PufaEmoji && window.PufaEmoji.flyTo) {
          // target the bead that NEWLY fills from this press (the fill is
          // position-based, so the current step's bead is already crimson)
          var beadIdx = Math.max(0, Math.min(cur + 1, segNodes.length - 1));
          window.PufaEmoji.flyTo(nextBtn, function () { return segNodes[beadIdx]; }, {
            ghost: true,
            onArrive: function () {
              haptic('tick');
              var b = segNodes[beadIdx];
              if (b && b.animate) { try { b.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.5)', offset: 0.4 }, { transform: 'scale(1)' }], { duration: 300, easing: 'ease-out' }); } catch (e) {} }
            }
          });
        }
        track('answer', { step: cur + 1, kind: step.kind, answer: answerOf(cur) });
        pressNext(); nextEmoji(); goTo(cur + 1, true, 'fwd');
      }
    });
    backBtn.addEventListener('click', function () { if (cur > 0) { track('back', { from: cur + 1, kind: steps[cur].kind }); goTo(cur - 1, true, 'back'); } });
    // What she answered on step i, as short text for analytics (option labels, never her free text).
    function answerOf(i) {
      var st = state[i] || {};
      var v = st.parts && st.parts.length ? st.parts.map(function (p) { return p.key + ': ' + p.value; }).join(' | ') : (st.value || '');
      return String(v).slice(0, 95) || 'skipped';
    }
    function packOf() {
      var r = bundle && bundle.querySelector('.pbb-row.is-selected'); if (!r) return {};
      return { qty: parseInt(r.getAttribute('data-qty'), 10) || 0, bonus: parseInt(r.getAttribute('data-bonus'), 10) || 0, value: (parseInt(r.getAttribute('data-pay'), 10) || 0) / 100, currency: 'ILS' };
    }
    // "Pufa plays for you": skipping is a ~1s visible roulette instead of a teleport -
    // random cards on the current step pulse in sequence, one hush-spark feeds the
    // ball, then we land on the quantity step. Purely theatrical: NO selections are
    // made (empty prefs = Pufa curates at fulfillment; random picks would constrain
    // the order). Any tap fast-forwards; reduced-motion keeps the instant jump.
    var skipPlaying = false;
    skipBtn.addEventListener('click', function () {
      if (cur >= qIndex || skipPlaying) return;
      track('skip', { step: cur + 1, kind: steps[cur].kind });
      var cards = steps[cur].el ? steps[cur].el.querySelectorAll('.iop-card:not(.iop-card--disabled), .owz-chip, .iop-size__step') : [];
      if (reduced || !cards.length) { goTo(qIndex, true, 'fwd'); return; }
      skipPlaying = true;
      var teaseEl2 = rootEl.querySelector('[data-owz-tease]') || document.querySelector('[data-owz-tease]');
      var teasePrev = teaseEl2 ? teaseEl2.textContent : null;
      if (teaseEl2) teaseEl2.textContent = 'פופה בוחרת... 🤫✨';
      var timers = [], done = false;
      function finishPlay() {
        if (done) return; done = true; skipPlaying = false;
        timers.forEach(clearTimeout);
        overlay.removeEventListener('click', fastFwd, true);
        if (teaseEl2 && teasePrev != null) setTimeout(function () { teaseEl2.textContent = teasePrev; }, 1600);
        goTo(qIndex, true, 'fwd');
      }
      function fastFwd(e) { e.stopPropagation(); e.preventDefault(); finishPlay(); }
      overlay.addEventListener('click', fastFwd, true);
      for (var pi = 0; pi < 4; pi++) {
        (function (pi2) {
          timers.push(setTimeout(function () {
            owzPing(cards[Math.floor(Math.random() * cards.length)], { count: 2 });
          }, 130 * pi2));
        })(pi);
      }
      timers.push(setTimeout(function () {
        feedFrom(cards[Math.floor(Math.random() * cards.length)], '🤫');
        timers.push(setTimeout(finishPlay, 520));
      }, 560));
    });

    var errTimer = null;
    function flashErr(msg) {
      if (!footErr) return;
      footErr.hidden = true; void footErr.offsetWidth; // restart the pop on a repeat press
      footErr.textContent = msg; footErr.hidden = false; sheet.classList.add('owz-has-toast');
      clearTimeout(errTimer); errTimer = setTimeout(function () { footErr.hidden = true; sheet.classList.remove('owz-has-toast'); }, 2600);
    }
    // Draws attention to the picker that is blocking Continue: a pulsing brand-colour ring
    // on the cards themselves, plus the sub-question. Adds no layout, so nothing reflows and
    // no scrollbar appears. Scrolled into view first, because on a grouped step the blocking
    // picker can be below the fold - pulsing something she cannot see helps nobody.
    var pulseTimer = null;
    function pulseNeeded(el) {
      if (!el) return;
      if (pulseTimer) clearTimeout(pulseTimer);
      Array.prototype.forEach.call(bodyEl.querySelectorAll('.owz-needs-pick'), function (n) { n.classList.remove('owz-needs-pick'); });
      try {
        var r = el.getBoundingClientRect(), br = bodyEl.getBoundingClientRect();
        if (r.top < br.top || r.bottom > br.bottom) {
          el.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
        }
      } catch (e) {}
      void el.offsetWidth;                       // restart the animation on a repeat press
      el.classList.add('owz-needs-pick');
      haptic('soft');
      pulseTimer = setTimeout(function () { el.classList.remove('owz-needs-pick'); }, 2200);
    }

    // ── Price (build-once spans) ─────────────────────────────────────────────
    // Free-capsule reminder: same localStorage arm as pufa-gift-capsule.js
    // (?gift=capsule, 7-day TTL). Rendered as a tiny chip ON the price line so the
    // gift reads as part of the order math on every step - not another footer row.
    function giftArmed() {
      try { var t = parseInt(localStorage.getItem('pufa_gift_capsule'), 10); return !!t && (Date.now() - t) <= 7 * 24 * 60 * 60 * 1000; } catch (e) { return false; }
    }
    var priceReady = false, pNow, pWas, pGift;
    function buildPrice() {
      // No bonus-count suffix here: the quantity row already says "5 + 2 מתנה = 7";
      // the total shows just the price, plus the separate free-capsule chip when armed.
      priceEl.innerHTML = '<span class="owz-price__k">' + esc(T('total', 'סה״כ')) + '</span><span class="owz-price__vals"><s class="owz-price__was"></s><span class="owz-price__now"></span><span class="owz-giftchip" hidden>' + esc(T('giftchip', '+ קפסולת מתנה 🎁')) + '</span></span>';
      pWas = priceEl.querySelector('.owz-price__was'); pNow = priceEl.querySelector('.owz-price__now'); pGift = priceEl.querySelector('.owz-giftchip'); pK = priceEl.querySelector('.owz-price__k');
    }
    var pK;
    function capsLabel(row) {
      var n = (parseInt(row.getAttribute('data-qty'), 10) || 0) + (parseInt(row.getAttribute('data-bonus'), 10) || 0);
      return n > 0 ? n + ' קפסולות' : T('total', 'סה״כ');
    }
    function updatePrice() {
      if (!pNow) buildPrice();
      var pay = '', was = '';
      if (bundle) {
        var row = bundle.querySelector('.pbb-row.is-selected'); if (!row) { priceReady = false; priceEl.hidden = true; return; }
        pay = ((row.querySelector('.pbb-row__pay') || {}).textContent || '').trim();
        was = ((row.querySelector('.pbb-row__was') || {}).textContent || '').trim();
        if (pK) pK.textContent = capsLabel(row);
      } else {
        // variant-priced product (no bundle): read the selected size/variant card's price
        var card = variantNode && variantNode.querySelector('.iop-card--selected');
        if (!card) { priceReady = false; priceEl.hidden = true; return; }
        var pe = card.querySelector('.iop-card__price');
        if (pe) { var cmp = pe.querySelector('.iop-card__compare'); was = cmp ? cmp.textContent.trim() : ''; pay = pe.textContent.replace(was, '').trim(); }
        if (!pay) { priceReady = false; priceEl.hidden = true; return; }
      }
      pNow.textContent = pay; pWas.textContent = was; pWas.style.display = was ? '' : 'none';
      // Gift-capsule chip: only when armed AND the order clears the ₪40 auto-add bar
      // (mirrors the drawer row, so the two indicators never disagree).
      if (pGift) {
        var payNum = parseFloat(String(pay).replace(/[^\d.]/g, '')) || 0;
        var showG = giftArmed() && payNum >= 40;
        if (showG && pGift.hidden && !reduced && pGift.animate) {
          try { pGift.animate([{ transform: 'scale(.6)' }, { transform: 'scale(1.15)', offset: 0.6 }, { transform: 'scale(1)' }], { duration: 350, easing: 'ease-out' }); } catch (e) {}
        }
        pGift.hidden = !showG;
      }
      priceReady = true; if (overlay.classList.contains('is-open')) priceEl.hidden = false;
    }

    // ── Review ───────────────────────────────────────────────────────────────
    function iconFor(title) { var t = title || ''; if (/אירוע/.test(t)) return '🎉'; if (/אורח/.test(t)) return '👥'; if (/כובד|בולט|מראה/.test(t)) return '💫'; if (/גוון|צבע/.test(t)) return '🎨'; if (/מידה/.test(t)) return '📏'; if (/סטייל|סגנון/.test(t)) return '✨'; if (/כמות|קסם|קפסול/.test(t)) return '🎁'; if (/גיל|בן כמה/.test(t)) return '🎂'; return '💎'; }
    function shortLabel(title) { var t = title || ''; if (/אירוע/.test(t)) return 'אירוע'; if (/אורח/.test(t)) return 'אורחים'; if (/כובד|בולט/.test(t)) return 'מראה'; if (/סגנון/.test(t)) return 'סגנון'; if (/סטייל/.test(t)) return 'סטייל'; if (/צבע/.test(t)) return 'צבע'; if (/גוון/.test(t)) return 'גוון'; if (/מידה/.test(t)) return 'מידה'; if (/סוג|תכשיט/.test(t)) return 'תכשיט'; if (/גיל|בן כמה/.test(t)) return 'גיל'; if (/כמות|קסם|קפסול/.test(t)) return 'כמות'; return t.split('\n')[0]; }
    function renderReviewInto(wrap, withData) {
      var html = '<div class="owz-review">';
      html += '<div class="owz-step__title">' + esc(reviewStep.title) + '</div>';
      html += '<div class="owz-step__help">' + esc(T('lockin_sub', 'רגע אחד ואתם בפנים 🥰')) + '</div>';
      html += '<div class="owz-review__list" data-owz-review-list></div>';
      html += '<p class="owz-review__note" data-owz-mix-note hidden></p>';
      if (cfg.trust_enabled !== false && cfg.trust && cfg.trust.length > 2) {
        html += '<div class="owz-trust" aria-hidden="true">';
        cfg.trust.forEach(function (it) { html += '<div class="owz-trust__item"><div class="owz-trust__icon">' + esc(it.icon) + '</div><div class="owz-trust__title">' + esc(it.title) + '</div><div class="owz-trust__sub">' + esc(it.sub) + '</div></div>'; });
        html += '</div>';
      }
      html += '<button type="button" class="owz-restart" data-owz-restart>' + esc(T('restart', 'להתחיל מחדש 🔄')) + '</button>';
      html += '</div>';
      // keep the SR heading; replace the rest
      var keep = wrap.querySelector('[data-owz-step-h]');
      wrap.innerHTML = ''; if (keep) wrap.appendChild(keep);
      wrap.insertAdjacentHTML('beforeend', html);
      var restartBtn = wrap.querySelector('[data-owz-restart]'); if (restartBtn) restartBtn.addEventListener('click', function () { track('restart', {}); resetWizard(true); });
      if (withData) {
        var list = wrap.querySelector('[data-owz-review-list]');
        // One row per key/value, tappable back to the step it came from.
        function addRow(i, icon, key, val, extraClass) {
          var line = document.createElement('div');
          line.className = 'owz-review__line' + (extraClass ? ' ' + extraClass : '');
          line.innerHTML = '<span class="owz-review__icon" aria-hidden="true">' + icon + '</span>'
            + '<span class="owz-review__k">' + esc(key) + '</span>'
            + '<span class="owz-review__v">' + esc(val) + '</span>';
          var edit = document.createElement('button');
          edit.type = 'button'; edit.className = 'owz-review__edit'; edit.textContent = T('edit', 'שינוי');
          edit.addEventListener('click', function () { track('edit', { to: i + 1, kind: steps[i].kind }); goTo(i, true, 'back'); });
          line.style.cursor = 'pointer';
          line.addEventListener('click', function (e) { if (!e.target.closest('.owz-review__edit')) { track('edit', { to: i + 1, kind: steps[i].kind }); goTo(i, true, 'back'); } });
          line.appendChild(edit); list.appendChild(line);
          return line;
        }
        steps.forEach(function (s, i) {
          if (s.kind === 'review') return;

          // A grouped step asked several questions; showing them as one comma list
          // ("עדין, לבבות") hides which token answered which, and hides the fact that an
          // untouched style picker means every style is in play. One row per child.
          if (s.kind === 'group' && state[i].parts && state[i].parts.length) {
            state[i].parts.forEach(function (p) { addRow(i, iconFor(p.key), shortLabel(p.key), p.value); });
            var freeVal = s.freeText && s.freeText();
            if (freeVal) addRow(i, '✏️', M('review_free_key', 'פירוט'), freeVal, 'owz-review__line--free');
            return;
          }

          var val = state[i].value || '';
          // Quantity clarity (owner): "10 קפסולות" hides the 5 free ones. The review line
          // must spell out paid + bonus = total so the real value is unmissable.
          if (s.kind === 'bundle' && bundle) {
            var selRow = bundle.querySelector('.pbb-row.is-selected');
            if (selRow) {
              var rq = parseInt(selRow.getAttribute('data-qty'), 10) || 0;
              var rb = parseInt(selRow.getAttribute('data-bonus'), 10) || 0;
              if (rb > 0) val = rq + ' + ' + rb + ' מתנה = ' + (rq + rb) + ' קפסולות 🎁';
            }
          }
          if (!val && (s.kind === 'text' || /סגנון|סטייל/.test(s.title || ''))) val = M('style_empty', 'הפתעה מלאה ✨');
          if (!val) { if (s.optional) return; val = '-'; } // hide empty OPTIONAL rows (e.g. unselected age); required falls back to '-'
          addRow(i, iconFor(s.title), shortLabel(s.title), val);
        });
        // Total savings once, big, at the decision moment (#13): replaces the per-row
        // strikethroughs. Computed from the selected tier's compare-at vs pay (agorot).
        if (bundle) {
          var savRow = bundle.querySelector('.pbb-row.is-selected');
          if (savRow) {
            var savWas = parseInt(savRow.getAttribute('data-was'), 10) || 0;
            var savPay = parseInt(savRow.getAttribute('data-pay'), 10) || 0;
            if (savWas > savPay) {
              var savEl = document.createElement('p');
              savEl.className = 'owz-review__savings';
              savEl.textContent = (document.body.classList.contains('pufa-vibe-men') ? 'חסכתם ₪' : 'חסכת ₪') + Math.round((savWas - savPay) / 100) + ' 🎉';
              list.parentNode.insertBefore(savEl, list.nextSibling);
            }
          }
        } else if (variantNode) {
          // Variant-priced products (party kit: no bundle step) get their own value
          // punchline (0.66.0). Everything is parsed from what the shopper already
          // sees - the selected kit card's label ("50 קפסולות") and price, and the
          // guests answer - and every line FAILS CLOSED: unparsable = not rendered.
          var vCard = variantNode.querySelector('.iop-card--selected');
          if (vCard) {
            var vQty = cardCaps(vCard);
            var vPayEl = vCard.querySelector('.iop-card__price');
            var vWasTxt = '', vPayTxt = '';
            if (vPayEl) { var vCmp = vPayEl.querySelector('.iop-card__compare'); vWasTxt = vCmp ? vCmp.textContent : ''; vPayTxt = vPayEl.textContent.replace(vWasTxt, ''); }
            var vPay = parseFloat(String(vPayTxt).replace(/[^\d.]/g, '')) || 0;
            var vWas = parseFloat(String(vWasTxt).replace(/[^\d.]/g, '')) || 0;
            var vFmt = function (n) { return '₪' + String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ','); };
            if (vWas > vPay && vPay > 0) {
              var vSavEl = document.createElement('p');
              vSavEl.className = 'owz-review__savings';
              vSavEl.textContent = 'חסכתם ' + vFmt(vWas - vPay) + ' 🎉';
              list.parentNode.insertBefore(vSavEl, list.nextSibling);
            }
            if (vQty > 0 && vPay > 0) {
              // Per-guest only with an honest divisor; open ranges ("עד 25") fall
              // through to the per-capsule frame via guestsFloor() = 0.
              var vGuests = guestsFloor();
              var vValEl = document.createElement('p');
              vValEl.className = 'owz-review__savings owz-review__savings--value';
              vValEl.textContent = (vGuests > 0 && vGuests <= vQty)
                ? 'הפתעה לכל אורח · בערך ' + vFmt(vPay / vGuests) + ' לאורח 🥳'
                : vQty + ' קפסולות · בערך ' + vFmt(vPay / vQty) + ' לקפסולה 🎉';
              list.parentNode.insertBefore(vValEl, list.nextSibling);
            }
          }
        }
        // When it arrives and what shipping costs, BEFORE she commits: the cart drawer used
        // to be the first place the ₪19.90 showed up. Date comes from the PDP's own promise
        // pill and the threshold from the theme setting, so neither fact has a second owner.
        var shipEl = document.createElement('p'); shipEl.className = 'owz-review__ship';
        var promise = document.querySelector('.pufa-ship');
        var when = promise ? promise.textContent.replace(/\s+/g, ' ').split('·')[0].trim() : '';
        var freeAt = 24900; try { freeAt = JSON.parse(document.querySelector('[data-pufa-campaigns]').textContent).freeShipAt || freeAt; } catch (e) {}
        var payNow = Math.round((packOf().value || 0) * 100);
        var shipTxt = !payNow ? '' : (payNow >= freeAt ? M('ship_free', 'משלוח חינם לנקודת איסוף 🎉') : M('ship_paid', 'משלוח מ-₪19.90, חינם מעל ₪{at}').replace('{at}', Math.round(freeAt / 100)));
        shipEl.innerHTML = (when ? '<b>🚚 ' + esc(when) + '</b>' : '') + (shipTxt ? '<span>' + esc(shipTxt) + '</span>' : '');
        // bundle products only: the party kit states its own delivery terms in the trust tiles
        if (bundle && (when || shipTxt)) { var afterEl = wrap.querySelector('.owz-review__savings') || list; afterEl.parentNode.insertBefore(shipEl, afterEl.nextSibling); }
        // Types-vs-capsules expectation guard: N types + fewer capsules must not read
        // as "one of each". Capsules counted as received (paid + bonus).
        var noteEl = wrap.querySelector('[data-owz-mix-note]');
        if (noteEl) {
          var typesCount = 0;
          steps.forEach(function (s, i) { if (s.kind === 'multi' && s.teaseKey === 'type') typesCount = state[i].count || 0; });
          var caps = 0;
          if (bundle) {
            var selRow = bundle.querySelector('.pbb-row.is-selected');
            if (selRow) caps = (parseInt(selRow.getAttribute('data-qty'), 10) || 0) + (parseInt(selRow.getAttribute('data-bonus'), 10) || 0);
          }
          var note = mixNote(typesCount, caps, MIC.mix_note_one, MIC.mix_note_many);
          // Styles have the mirror-image problem to types: ONE style across many capsules
          // means every piece shares a theme. That is a legitimate choice, but she should
          // be told before she pays rather than discover it in the box.
          if (!note && caps > 1) {
            var styleStep = null, styleVal = '';
            steps.forEach(function (s, i) {
              if (s.kind !== 'group' || !state[i].parts) return;
              state[i].parts.forEach(function (p) { if (p.key === 'סגנון') { styleStep = s; styleVal = p.value; } });
            });
            var picked = styleStep && styleVal && styleVal.indexOf('הפתעה') === -1
              ? styleVal.split(',').length : 0;
            if (picked === 1) {
              note = (MIC.style_note_one || 'סימנת סגנון אחד ותקבלי {caps} תכשיטים - כולם מ{styles}, שונים זה מזה אבל מאותו סגנון. רוצה גיוון? סמני עוד סגנון ✨')
                .replace(/\{caps\}/g, caps).replace(/\{styles\}/g, styleVal.trim());
            }
          }
          noteEl.textContent = note; noteEl.hidden = !note;
        }
        updatePrice();
      }
    }
    var sealed = false; function sealOnce() { sealed = true; }

    // ── Lock in: optimistic submit + confirmed handoff ───────────────────────
    var submitted = false;
    function lockIn() {
      for (var i = 0; i < steps.length; i++) { var s = steps[i];
        if (s.kind === 'review' || s.kind === 'text') continue; if (s.kind === 'choice' && s.optional) continue;
        if (!isReady(i)) { goTo(i, true, 'back'); return; } }
      if (submitted) return; submitted = true; track('complete', packOf());
      // Hand the bonus count to the cart drawer so it can celebrate the free capsules landing.
      try {
        var bSel = bundle && bundle.querySelector('.pbb-row.is-selected');
        var bN = bSel ? (parseInt(bSel.getAttribute('data-bonus'), 10) || 0) : 0;
        if (bN > 0) sessionStorage.setItem('pufa_bonus_land', bN + ':' + Date.now());
      } catch (e) {}
      var handled = false, subs = [];
      function clean() { subs.forEach(function (u) { try { u && u(); } catch (e) {} }); }
      // Only OUR add confirms success: the theme publishes cartUpdate from cart-items,
      // quick-add and the gift script too - an unrelated event must not fake a win
      // (and then swallow the real cartError with handled=true).
      function onOk(evt) { if (evt && evt.source && evt.source !== 'product-form') return; if (handled) return; handled = true; clean(); track('add_ok', packOf()); setTimeout(function () { close('done'); resetWizard(false); }, reduced ? 0 : 900); }
      function onErr() { if (handled) return; handled = true; clean(); track('add_error', {}); submitted = false; try { sessionStorage.removeItem('pufa_bonus_land'); } catch (e) {} stopConfetti(); climaxEl.classList.remove('is-on'); if ('inert' in bodyEl) { bodyEl.inert = false; footEl.inert = false; } flashErr(M('err', 'רגע, משהו השתבש 😅 ננסה שוב?')); announce(M('err', 'שגיאה')); refreshNav(); }
      // PUB_SUB_EVENTS is a `const` global (NOT on window) - resolve bare identifiers,
      // otherwise this branch never runs and lock-in blind-closes at 900ms with no
      // error handling (the latent bug behind "wizard closes even when the add fails").
      var PSE = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS) || window.PUB_SUB_EVENTS;
      var SUB = (typeof subscribe === 'function' && subscribe) || window.subscribe;
      if (typeof SUB === 'function' && PSE) {
        try { subs.push(SUB(PSE.cartUpdate, onOk)); } catch (e) {}
        try { subs.push(SUB(PSE.cartError, onErr)); } catch (e) {}
        setTimeout(function () { if (!handled) { handled = true; clean(); close('done'); resetWizard(false); } }, 4000);
      } else { setTimeout(function () { close('done'); resetWizard(false); }, reduced ? 200 : 900); }
      celebrate(function () {});
      try { form.requestSubmit(); } catch (e) { var sub = form && form.querySelector('[type="submit"][name="add"]'); if (sub) sub.click(); }
    }

    // ── Climax + confetti ────────────────────────────────────────────────────
    var rafToken = null, confTimer = null;
    function stopConfetti() { if (rafToken) { cancelAnimationFrame(rafToken); rafToken = null; } if (confTimer) { clearTimeout(confTimer); confTimer = null; } var c = sheet.querySelector('.owz-confetti-canvas'); if (c) c.remove(); }
    function fireConfetti(originEl) {
      stopConfetti(); if (reduced || cfg.confetti === false) return;
      var box = sheet.getBoundingClientRect(); var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var cv = document.createElement('canvas'); cv.className = 'owz-confetti-canvas'; cv.setAttribute('aria-hidden', 'true');
      cv.width = box.width * dpr; cv.height = box.height * dpr; sheet.appendChild(cv);
      var ctx = cv.getContext('2d'); if (!ctx) { cv.remove(); return; }
      var o = (originEl || sheet).getBoundingClientRect();
      var ox = (o.left - box.left + o.width / 2) * dpr, oy = (o.top - box.top + o.height / 2) * dpr;
      var colors = cfg.confetti_colors || ['#c70d38', '#e0b64a', '#fbe3ea', '#ffffff'];
      var P = []; for (var i = 0; i < CONFETTI_MAX; i++) { var a = (i / CONFETTI_MAX) * Math.PI * 2, sp = (3 + (i % 5)) * dpr;
        P.push({ x: ox, y: oy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 3 * dpr, g: 0.18 * dpr, rot: i, vr: ((i % 7) - 3) * 0.08, sz: (6 + (i % 5)) * dpr, c: colors[i % colors.length], life: 70 }); }
      function frame() { ctx.clearRect(0, 0, cv.width, cv.height); var alive = false;
        for (var k = 0; k < P.length; k++) { var p = P[k]; if (p.life <= 0) continue; alive = true; p.vy += p.g; p.vx *= 0.99; p.x += p.vx; p.y += p.vy; p.rot += p.vr; p.life--;
          ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot); ctx.globalAlpha = Math.max(0, p.life / 70); ctx.fillStyle = p.c; ctx.fillRect(-p.sz / 2, -p.sz / 2, p.sz, p.sz * 0.6); ctx.restore(); }
        if (alive) rafToken = requestAnimationFrame(frame); else stopConfetti(); }
      rafToken = requestAnimationFrame(frame); confTimer = setTimeout(stopConfetti, 1600);
    }
    function celebrate(done) {
      if (!climaxEl) { done(); return; }
      // The item has entered the CART - nothing is paid for and nothing is shipping. The
      // old line said "on the way", which was both false and a completion cue at the exact
      // moment she still has to check out. State + gap + agency instead: it is sealed, it
      // is in the cart, and one more step makes it hers.
      var msg = climaxEl.querySelector('[data-owz-climax-msg]'); if (msg) msg.textContent = T('climax', 'ההפתעה שלך סגורה ומחכה בסל 🎁');
      var sub = climaxEl.querySelector('[data-owz-climax-sub]'); if (sub) sub.textContent = T('climax_sub', 'עוד צעד קטן והיא שלך ✨');
      if ('inert' in bodyEl) { try { bodyEl.inert = true; footEl.inert = true; } catch (e) {} }
      var cap = climaxEl.querySelector('.owz-climax__cap');
      if (cap) cap.classList.remove('is-open');
      climaxEl.classList.add('is-on');
      // Its OWN key, not `climax`. Both lines used to read the same key, so the divergent
      // fallback below never ran and screen-reader users heard the visual line verbatim,
      // emoji names and all. They also get no animation and no drawer they can see, so the
      // spoken version has to carry orientation the sighted user reads off the screen.
      if (liveEl) { liveEl.textContent = ''; requestAnimationFrame(function () { liveEl.textContent = T('climax_sr', 'ההפתעה שלך נוספה לסל. סל הקניות נפתח, אפשר לעבור לתשלום.'); }); }
      if (reduced) {
        if (cap) cap.classList.add('is-open');
      } else {
        // Beat 2, right after the gacha drop lands: the shells crack open around the
        // gift, and only THEN the win effects fire (haptic + confetti + cart pulse).
        setTimeout(function () {
          if (!climaxEl.classList.contains('is-on')) return;
          if (cap) cap.classList.add('is-open');
          haptic('win'); fireConfetti(cap || climaxEl); pulseCart();
        }, 820);
      }
      setTimeout(function () { if ('inert' in bodyEl) { try { bodyEl.inert = false; footEl.inert = false; } catch (e) {} } done(); }, reduced ? 200 : 950);
      // The visual lingers past the flow handoff; close() hides the sheet anyway.
      setTimeout(function () { climaxEl.classList.remove('is-on'); if (cap) cap.classList.remove('is-open'); }, reduced ? 220 : 2000);
    }
    function pulseCart() { var c = document.querySelector('#cart-icon-bubble, .cart-count-bubble, [href$="/cart"]'); if (c) { c.animate && c.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 500 }); } }

    // ── Open / close + focus + scroll lock ───────────────────────────────────
    var lastFocus = null, savedY = 0;
    function lockScroll() { savedY = window.scrollY || window.pageYOffset || 0; document.body.style.position = 'fixed'; document.body.style.top = (-savedY) + 'px'; document.body.style.left = '0'; document.body.style.right = '0'; document.body.style.width = '100%'; }
    function unlockScroll() { document.body.style.position = ''; document.body.style.top = ''; document.body.style.left = ''; document.body.style.right = ''; document.body.style.width = ''; window.scrollTo(0, savedY); }
    // Mobile back gesture: open() pushes {owz:1}; popstate closes the sheet instead of
    // leaving the page. A manual close consumes that entry via history.back() behind
    // `popGuard` so the resulting popstate is a no-op. popstate while closed = no-op.
    var popGuard = false;
    function onPop() {
      if (popGuard) { popGuard = false; return; }
      if (overlay.classList.contains('is-open')) { track('close', { step: cur + 1, kind: steps[cur].kind, method: 'back_gesture' }); teardown(); restoreFocus(); submitted = false; saveOpen(false); }
    }
    window.addEventListener('popstate', onPop);
    // bfcache: a page restored via Back must never come back with a locked body / open sheet
    // (the lock-in flow can navigate to cart before the close timers finish). A restored page
    // also can't have an add in flight, so re-arm `submitted` - otherwise a heap restored
    // mid-lock-in leaves the review step's button permanently dead.
    window.addEventListener('pageshow', function (e) { if (e.persisted) { hardClose(); submitted = false; } });
    function open() {
      lastFocus = document.activeElement;
      overlay.classList.add('is-open'); overlay.removeAttribute('aria-hidden');
      lockScroll();
      try { if (!(history.state && history.state.owz)) history.pushState({ owz: 1 }, ''); } catch (e) {}
      document.addEventListener('keydown', onKey);
      if (cur >= steps.length - 1) cur = Math.min(cur, steps.length - 1);
      goTo(cur, false, 'fwd');
      if (priceReady) priceEl.hidden = false;
      setTimeout(function () { var h = steps[cur].el.querySelector('[data-owz-step-h]'); (h || sheet).focus && (h || sheet).focus(); }, 60);
      track('open', { step: cur + 1 }); saveOpen(true);
    }
    function teardown() { overlay.classList.remove('is-open'); overlay.setAttribute('aria-hidden', 'true'); document.removeEventListener('keydown', onKey); clearTimeout(advanceTimer); stopConfetti(); unlockScroll(); }
    function restoreFocus() {
      if (lastFocus && document.contains(lastFocus) && lastFocus.offsetParent !== null) { try { lastFocus.focus({ preventScroll: true }); return; } catch (e) {} }
      var fb = document.querySelector('#cart-icon-bubble, .cart-count-bubble, [href$="/cart"]') || document.querySelector('h1');
      if (fb && fb.focus) { try { fb.focus({ preventScroll: true }); } catch (e) {} }
    }
    // close() also re-arms `submitted`: this is a short in-flight debounce (the lock-in
    // close fires ~900ms after submit) so a silently-failed add can be retried on re-open.
    function close(why) {
      var how = typeof why === 'string' ? why : 'button';
      if (how !== 'done' && overlay.classList.contains('is-open')) track('close', { step: cur + 1, kind: steps[cur].kind, method: how });
      teardown(); restoreFocus(); submitted = false; saveOpen(false);
      try { if (history.state && history.state.owz) { popGuard = true; history.back(); } } catch (e) {}
    }
    // Start a clean order: clear the user-entered OPTIONAL inputs (style free-text, age/guests),
    // re-sync every step, drop the saved progress, and (when open) jump back to step 1.
    function resetWizard(navigate) {
      submitted = false;
      steps.forEach(function (s) {
        if (s.el) s.el.classList.remove('is-active'); // clear stale active step so start-over doesn't leave the review overlaid
        // 'group' as well as 'text': since the free-text picker moved inside the style
        // group this branch stopped firing, so her words survived both "start over" AND
        // the post-purchase reset - the next capsule added in the same session carried the
        // previous order's note. The input dispatch is load-bearing: it is what flips the
        // hidden mirror input back to disabled.
        if ((s.kind === 'text' || s.kind === 'group') && s.node) { var ta = s.node.querySelector('.iop-textarea'); if (ta && ta.value) { ta.value = ''; ta.dispatchEvent(new Event('input', { bubbles: true })); } }
        else if (s.kind === 'choice' && s.el) { s.el.querySelectorAll('[data-owz-chip].is-on').forEach(function (c) { c.classList.remove('is-on'); c.setAttribute('aria-checked', 'false'); }); if (s.extra) setHiddenProp(form, s.extra.property_name, ''); }
        if (s._sync) s._sync(false);
      });
      try { sessionStorage.removeItem(saveKey); } catch (e) {}
      var rh = cta.querySelector('[data-owz-resume]'); if (rh) rh.hidden = true;
      cur = 0;
      if (navigate) goTo(0, true, 'back');
    }
    function hardClose() { teardown(); }
    var imagesWarmed = false;
    function warmImages() {
      if (imagesWarmed) return;
      imagesWarmed = true;
      overlay.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    }
    ['pointerenter', 'pointerdown', 'focus'].forEach(function (ev) { cta.addEventListener(ev, warmImages, { once: true, passive: true }); });
    if ('IntersectionObserver' in window) {
      var warmIo = new IntersectionObserver(function (entries) {
        if (entries.some(function (en) { return en.isIntersecting; })) { warmIo.disconnect(); warmImages(); }
      }, { rootMargin: '300px 0px' });
      warmIo.observe(cta);
    }
    cta.addEventListener('click', function () { warmImages(); ctaEruption(); open(); });
    function selectBundleQty(qty) {
      if (!bundle || !qty) return;
      var row = bundle.querySelector('.pbb-row[data-qty="' + qty + '"]');
      if (row && !row.classList.contains('is-selected')) row.click();
    }
    rootEl.addEventListener('owz:open', function (e) {
      warmImages();
      if (!overlay.classList.contains('is-open')) { ctaEruption(); open(); }
      selectBundleQty(e.detail && e.detail.qty);
    });
    rootEl.querySelectorAll('[data-owz-close]').forEach(function (x) { x.addEventListener('click', function () { close(x.hasAttribute('data-owz-grab') ? 'grab' : 'x'); }); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay || e.target.hasAttribute('data-owz-backdrop')) close('backdrop'); });
    function onKey(e) { if (!overlay.classList.contains('is-open')) return; if (e.key === 'Escape') { close('esc'); return; } if (e.key === 'Tab') trapFocus(e); }
    function trapFocus(e) {
      var all = sheet.querySelectorAll(FOCUSABLE), first = null, last = null;
      for (var i = 0; i < all.length; i++) { var el = all[i]; if (el.offsetParent !== null && !el.hasAttribute('disabled')) { if (!first) first = el; last = el; } }
      if (!first) return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    // ── Entry CTA: idle emoji emitter ("charging up") + click eruption ───────
    function startCtaEmitter() {
      cta.classList.add('owz-emitting');
      var timer = null;
      function rise() {
        if (document.hidden || overlay.classList.contains('is-open') || !window.PufaEmoji) return; // skip behind the open modal / background tabs
        var r = cta.getBoundingClientRect(); if (!r.width) return;
        // one particle from the button edge, flying outward (shared engine = fixed-position, no overflow)
        window.PufaEmoji.burst(r, { count: 1, spread: 'edge', size: 19 + Math.random() * 12, dist: 28 + Math.random() * 40, spin: 90, life: 1200, dur: 1150 });
      }
      function play() { if (!timer) { rise(); rise(); timer = setInterval(rise, 320); } }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      var obs = null;
      if ('IntersectionObserver' in window) {
        obs = new IntersectionObserver(function (ents) { ents.forEach(function (en) { en.isIntersecting ? play() : stop(); }); }, { threshold: 0.25 });
        obs.observe(cta);
      } else { play(); }
      // Tear down the interval + observer if this section is unloaded (theme editor reloads).
      rootEl._owzCleanup = function () { stop(); if (obs) { try { obs.disconnect(); } catch (e) {} } };
    }
    function ctaEruption() {
      if (reduced || !window.PufaEmoji) return;
      // Distributed burst: emojis pour out of random points across the WHOLE button surface.
      window.PufaEmoji.burst(cta.getBoundingClientRect(), {
        count: 30, spread: 'surface', rise: 22, spin: 130, life: 1150, dur: 1050,
        size: function () { return 22 + Math.random() * 16; },
        dist: function () { return 40 + Math.random() * 90; }
      });
    }

    // ── Swipe to dismiss (mobile) ────────────────────────────────────────────
    // Only a DELIBERATE downward drag counts. A tap (or the tiny finger travel when
    // tapping a lower card) must never add is-dragging or translate the sheet, which
    // would nudge it down + spring back. We wait for DRAG_MIN px of travel first.
    (function () {
      var startY = null, dy = 0, dragging = false, DRAG_MIN = 10;
      overlay.addEventListener('touchstart', function (e) {
        if (window.innerWidth >= DESKTOP) return;
        if (!e.target.closest('[data-owz-grab]') && !(e.target.closest('[data-owz-sheet]') && bodyEl.scrollTop <= 0)) return;
        if (bodyEl.scrollTop > 0) return; startY = e.touches[0].clientY; dy = 0; dragging = false;
      }, { passive: true });
      // Non-passive on purpose: once a drag is promoted, preventDefault() stops the browser
      // from also rubber-banding / pull-to-refreshing underneath the tracked sheet.
      overlay.addEventListener('touchmove', function (e) {
        if (startY === null) return;
        dy = e.touches[0].clientY - startY;
        if (!dragging) { if (dy < DRAG_MIN) return; dragging = true; sheet.classList.add('is-dragging'); } // promote to a drag only past the threshold
        if (e.cancelable) e.preventDefault();
        sheet.style.transform = dy > 0 ? 'translateY(' + dy + 'px)' : '';
      }, { passive: false });
      overlay.addEventListener('touchend', function () {
        if (startY === null) return;
        if (dragging) { sheet.classList.remove('is-dragging'); sheet.style.transform = ''; if (dy > SWIPE_PX) close('swipe'); }
        startY = null; dy = 0; dragging = false;
      });
    })();

    // ── Helpers: announce / haptic / track / persistence ─────────────────────
    function announce(msg) { if (liveEl) { liveEl.textContent = ''; requestAnimationFrame(function () { liveEl.textContent = msg || ''; }); } }
    var lastBuzz = 0;
    function haptic(kind) { if (cfg.haptics === false || !navigator.vibrate || document.hidden) return; if (performance.now() - lastBuzz < 40) return; lastBuzz = performance.now(); var p = kind === 'win' ? [15, 40, 30] : (kind === 'beat' ? 18 : (kind === 'soft' ? 8 : 10)); try { navigator.vibrate(p); } catch (e) {} }
    function track(action, extra) { var detail = Object.assign({ action: action, product: cfg.product_id }, extra || {}); try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'pufa_wizard' }, detail)); } catch (e) {} try { if (typeof window.gtag === 'function') window.gtag('event', 'pufa_wizard_' + action, detail); } catch (e) {} try { if (typeof window.fbq === 'function') window.fbq('trackCustom', 'pufa_wizard_' + action, detail); } catch (e) {} try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('pufa_wizard', detail); } catch (e) {} document.dispatchEvent(new CustomEvent('pufa:wizard', { detail: detail })); }
    var saveKey = 'pufa_wizard_' + (cfg.product_id || 'p');
    function save() { try { sessionStorage.setItem(saveKey, JSON.stringify({ step: cur })); } catch (e) {} }
    function saveOpen(o) { try { var s = JSON.parse(sessionStorage.getItem(saveKey) || '{}'); s.open = o; s.step = cur; sessionStorage.setItem(saveKey, JSON.stringify(s)); } catch (e) {} }
    function restoreHint() { try { var s = JSON.parse(sessionStorage.getItem(saveKey) || '{}'); if (s && s.step > 0 && s.step < steps.length - 1) { cur = s.step; var r = cta.querySelector('[data-owz-resume]'); if (r) { r.textContent = T('resume', 'ממשיכים') + ' · ' + (s.step + 1) + '/' + (steps.length - 1); r.hidden = false; var sub = cta.querySelector('[data-owz-cta-sub]'); if (sub) sub.hidden = true; } } } catch (e) {}
    }

    // ── Misc DOM helpers ─────────────────────────────────────────────────────
    function setHiddenProp(form, name, value) {
      if (!form || !name) return; var id = 'owz-prop-' + name.replace(/[^a-z0-9]/gi, '');
      var input = form.querySelector('#' + id);
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'properties[' + name + ']'; input.id = id; form.appendChild(input); }
      input.value = value; input.disabled = value === '';
    }
    // A grouped step may end with a free-text picker. Seed it with the labels the shopper
    // just ticked, shown as the PLACEHOLDER rather than as the value.
    //
    // Seeding the value instead was the first attempt and it was wrong: the box then held
    // the same words as כובד/סגנון, so the moment she touched it at all the cart carried
    // "כובד: עדין" and "פירוט הסגנון: עדין" saying the same thing. Suppressing the
    // duplicate needed a comparison against the last auto value on a timer, which is
    // fragile. A placeholder removes the duplication at the source: the field is empty
    // until she types, so anything submitted is purely additive.
    function attachGroupTextHint(step) {
      var ta = step.node.querySelector('.iop-textarea');
      if (!ta) return;
      // Re-entry guard, matching iopInit / iopTaInit in dynamic-picker. Without it a second
      // pass would capture the already-rewritten "סימנת: …" string as the base and the
      // empty-state placeholder would be poisoned permanently.
      if (ta.dataset.owzGroupHint === '1') return;
      ta.dataset.owzGroupHint = '1';
      var basePlaceholder = ta.getAttribute('placeholder') || '';

      function ticked() {
        var out = [];
        Array.prototype.forEach.call(step.node.querySelectorAll('.iop-card--selected'), function (c) {
          var v = c.getAttribute('data-label') || c.getAttribute('data-value') || '';
          if (v) out.push(v);
        });
        return out.join(', ');
      }
      function reflect() {
        var t = ticked();
        ta.setAttribute('placeholder', t ? ('סימנת: ' + t + '. אפשר להוסיף כאן פרטים ✨') : basePlaceholder);
      }

      step.node.addEventListener('option-picker:changed', function () { setTimeout(reflect, 0); });
      reflect();

      // Whatever she actually wrote. The review renders one row per STEP and this picker
      // lives inside the group, so without this her words never reach the confirm screen.
      step.freeText = function () { return ta.value.trim(); };
    }

    function attachStyleChips(wrap, iop, chips) {
      if (!chips || !chips.length) return; var ta = iop.querySelector('.iop-textarea'); if (!ta) return;
      var row = document.createElement('div'); row.className = 'owz-chips';
      var chipEls = [];
      // The chips' pressed state is DERIVED from the textarea (the source of truth), so they can
      // never get stuck "pressed but empty" - manual edits, resets and re-adds all keep them in sync.
      function reflect() {
        var parts = ta.value.split(',').map(function (s) { return s.trim(); });
        chipEls.forEach(function (chip) { var on = parts.indexOf(chip.getAttribute('data-owz-style')) >= 0; chip.classList.toggle('is-on', on); chip.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      }
      // Preset cards (issue #16, simplified per owner review): clean uniform cards, same
      // toggle/textarea contract; matching uses data-owz-style so the check badge can't
      // break it. Per-style personality lives in the emoji burst, not the frame.
      // (The 0.50.19 "velvet tray" wrapper was cut per owner review: cluttered, redundant copy.)
      row.classList.add('owz-chips--grid');
      chips.forEach(function (c) { c = (c || '').trim(); if (!c) return;
        var chip = document.createElement('button'); chip.type = 'button';
        chip.className = 'owz-chip owz-stylechip owz-preset';
        chip.setAttribute('data-owz-style', c);
        chip.innerHTML = '<span class="owz-preset__label">' + esc(c) + '</span><span class="owz-preset__check" aria-hidden="true">✓</span>';
        chip.setAttribute('aria-pressed', 'false');
        chip.addEventListener('click', function () {
          var parts = ta.value.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
          var i = parts.indexOf(c); if (i < 0) parts.push(c); else parts.splice(i, 1);
          ta.value = parts.join(', '); ta.dispatchEvent(new Event('input', { bubbles: true }));
        });
        chipEls.push(chip); row.appendChild(chip);
      });
      ta.addEventListener('input', reflect);
      ta.parentNode.insertBefore(row, ta);
      reflect();
    }

    // ── Portal to <body>, carry vars + dir (theme transforms break fixed) ────
    if (overlay.parentNode !== document.body) {
      overlay.style.cssText += ';' + (rootEl.getAttribute('style') || '');
      overlay.setAttribute('dir', 'rtl');
      document.body.appendChild(overlay);
    }

    // ── Activate: reveal CTA, hide inline (progressive enhancement) ──────────
    document.documentElement.classList.add('owz-on');
    document.documentElement.classList.remove('owz-armed'); // hand off from the anti-flash guard to the real hidden state
    var productFormEl = document.querySelector('product-form');
    if (productFormEl) {
      productFormEl.style.display = 'none'; productFormEl.setAttribute('aria-hidden', 'true'); if ('inert' in productFormEl) { try { productFormEl.inert = true; } catch (e) {} }
      // The payment-method icons render next to the native ATC (outside <product-form>); hide
      // them in this product's section so they don't show orphaned without the real button.
      // The footer payment row lives in a different section and is left untouched.
      var pfScope = productFormEl.closest('[id^="shopify-section"]') || document.body;
      pfScope.querySelectorAll('.pufa-payment-icons').forEach(function (el) { el.style.display = 'none'; el.setAttribute('aria-hidden', 'true'); });
    }
    (cfg.hide_selectors || []).forEach(function (sel) { try { document.querySelectorAll(String(sel).trim()).forEach(function (el) { el.style.display = 'none'; el.setAttribute('aria-hidden', 'true'); }); } catch (e) {} });
    restoreHint();
    cta.hidden = false;
    if (!reduced) startCtaEmitter();
    updatePrice();
    paintBeads();

    // Re-seed once the adopted pickers have finished their own init (size slider
    // render, bundle default selection, dynamic-picker preselects) so completeness
    // and price reflect reality even if they initialized after us.
    setTimeout(function () { steps.forEach(function (s) { if (s._sync) s._sync(false); }); if (steps[cur]) refreshNav(); paintBeads(); }, 140);

    // Quiz continuity: the home quiz sends ?ref=quiz&recipient=self|gift to the PDP.
    // Auto-open the wizard once per session so the flow continues seamlessly; Back /
    // refresh must never re-pop it (sessionStorage guard). 350ms waits out the 140ms
    // picker re-seed above so price/steps are correct on entry.
    try {
      var qFromQuiz = /[?&]ref=quiz\b/.test(location.search);
      var qRecipient = (location.search.match(/[?&]recipient=(self|gift)\b/) || [])[1] || '';
      if (qFromQuiz) {
        if (qRecipient === 'self') {
          // The age step's default sub mentions gifts; a self-buyer just said it's for her.
          // Steps are already built at this point, so patch the rendered element.
          steps.forEach(function (s) {
            if (s.kind !== 'choice' || !s.el) return;
            var help = s.el.querySelector('.owz-step__help');
            if (help && help.textContent.indexOf('במתנה') !== -1) help.textContent = 'ככה נדייק לך בול ✨';
          });
        }
        var qoKey = saveKey + '_qo', qoSeen = false;
        try { qoSeen = sessionStorage.getItem(qoKey) === '1'; } catch (e) {}
        if (!qoSeen) {
          try { sessionStorage.setItem(qoKey, '1'); } catch (e) {}
          var teaseEl = rootEl.querySelector('[data-owz-tease]') || document.querySelector('[data-owz-tease]');
          if (teaseEl) teaseEl.textContent = 'ממשיכים בדיוק מאיפה שעצרנו ✨';
          setTimeout(function () { open(); track('auto_open', { recipient: qRecipient }); }, 350);
        }
      }
    } catch (e) {}
  }

  function init(rootEl) { try { initImpl(rootEl); } catch (e) { try { console.error('OWZ-INIT-ERR:', e && (e.stack || e.message)); } catch (_) {} } }
  function initAll() { document.querySelectorAll('[data-owz]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelectorAll) e.target.querySelectorAll('[data-owz]').forEach(function (el) { if (el._owzCleanup) el._owzCleanup(); });
  });
})(typeof window !== 'undefined' ? window : globalThis);
